/**
 * SafeDayTab.jsx  — "Safe Day" SPY strategy assistant
 *
 * Strategy A: 15-Minute Opening Range Breakout (ORB)
 * Strategy B: VWAP + 9 EMA Alignment (5-min chart)
 * Exit Plan:  MACD + MA crossover signals, position tracker, alerts
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { playBeep, playAudio, DEFAULT_SAFE_DAY_ALERT } from '../../hooks/useAlertEngine';
import { tierGetRingtone, tierPutRingtone, tierDeleteRingtone, BASE, getToken, ibkrFetchOptionFlow } from '../../lib/api';
import AdvicePanel from './AdvicePanel';
import { aggregateBars, calcEMA, calcEMASeries, calcMACD, calcMACross, calcStochastic, calcRSI, minsRequired, estimateDebitPnl, calcBBWidth, floorStrike, ceilStrike, spreadWidth } from '../../lib/indicators';
import { classifySessionRegime, analyzePreMarket } from '../../lib/sessionRegime';


// ── Shared sub-components ─────────────────────────────────────────────────────

/** Coloured chip — regime / status badge */
export function Chip({ label, color, bg }) {
  return (
    <span style={{
      fontSize: 9, fontWeight: 800, letterSpacing: '.08em', padding: '2px 8px',
      borderRadius: 3, border: `1px solid ${color}50`,
      color, background: bg ?? color + '18',
    }}>
      {label}
    </span>
  );
}

/** A single data metric cell */
export function Metric({ label, value, sub, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--faint)' }}>
        {label}
      </span>
      <span style={{ fontSize: 15, fontWeight: 800, fontFamily: 'monospace', color: color ?? 'var(--text)' }}>
        {value}
      </span>
      {sub && <span style={{ fontSize: 9, color: 'var(--faint)' }}>{sub}</span>}
    </div>
  );
}

/** Spread suggestion box */
export function SpreadSuggestion({ type, sellStrike, buyStrike, refLabel, refValue, color }) {
  const isBull = type === 'bull_put' || type === 'call_debit';
  const title  = isBull ? 'Sell Bull Put Spread' : 'Sell Bear Call Spread';
  const inst   = isBull ? 'SELL put · BUY put' : 'SELL call · BUY call';
  const optType = isBull ? 'P' : 'C';
  return (
    <div style={{
      padding: '12px 14px', borderRadius: 7,
      border: `1px solid ${color}`,
      background: color + '0e',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 800, color, letterSpacing: '.04em' }}>
          {isBull ? '↑' : '↓'} {title}
        </span>
        <span style={{ fontSize: 9, color: 'var(--faint)', fontFamily: 'monospace' }}>
          {inst}
        </span>
      </div>

      {/* Strike display */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 9, color: 'var(--faint)', marginBottom: 2 }}>SELL (short leg)</div>
          <div style={{ fontSize: 22, fontWeight: 900, fontFamily: 'monospace', color }}>
            ${sellStrike}{optType}
          </div>
        </div>
        <div style={{ fontSize: 18, color: 'var(--faint)' }}>/</div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 9, color: 'var(--faint)', marginBottom: 2 }}>BUY (long leg)</div>
          <div style={{ fontSize: 22, fontWeight: 900, fontFamily: 'monospace', color: 'var(--muted)' }}>
            ${buyStrike}{optType}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontSize: 9, color: 'var(--faint)' }}>Width</div>
          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: 'monospace', color: 'var(--muted)' }}>
            ${Math.abs(sellStrike - buyStrike)}
          </div>
        </div>
      </div>

      {/* Reference level note */}
      <div style={{ fontSize: 10, color: 'var(--faint)', borderTop: '1px solid var(--border)', paddingTop: 7, lineHeight: 1.5 }}>
        {refLabel} <span style={{ color: 'var(--muted)', fontFamily: 'monospace' }}>${refValue}</span>
        {' — '}short leg placed {isBull ? 'below' : 'above'} this level.
        <br/>
        <span style={{ color: 'var(--warn)', fontStyle: 'italic' }}>
          Check live option chain for premium &amp; liquidity before entering.
        </span>
      </div>
    </div>
  );
}

// ── Strategy A: ORB ───────────────────────────────────────────────────────────
export function StrategyA({ bars, minutesIn, price, atrPct, ticker="SPY" }) {
  const ORB_MINS = 15;
  const established = minutesIn >= ORB_MINS;

  // Waiting — show the live forming range
  if (!established) {
    const formingHigh = bars.length ? Math.max(...bars.map(b => b.high)) : null;
    const formingLow  = bars.length ? Math.min(...bars.map(b => b.low))  : null;
    return (
      <Panel title="Strategy A — Opening Range Breakout" chip={<Chip label="FORMING" color="var(--warn)" />}>
        <div style={{ fontSize: 11, color: 'var(--faint)', marginBottom: 14, lineHeight: 1.5 }}>
          The opening range uses the first 15 minutes of trading to define a high and low.
          A breakout above or below this band signals the day's direction.
          Wait until <strong style={{ color: 'var(--text)' }}>9:45 ET</strong> before acting.
        </div>
        <div style={{ display: 'flex', gap: 24 }}>
          {formingHigh != null && <Metric label="Range high (so far)" value={`$${formingHigh.toFixed(2)}`} color="var(--up)" />}
          {formingLow  != null && <Metric label="Range low (so far)"  value={`$${formingLow.toFixed(2)}`}  color="var(--down)" />}
          <Metric label="Time remaining" value={`${ORB_MINS - minutesIn}min`} color="var(--warn)" />
        </div>
      </Panel>
    );
  }

  const orbBars  = bars.slice(0, ORB_MINS);
  const orbHigh  = Math.max(...orbBars.map(b => b.high));
  const orbLow   = Math.min(...orbBars.map(b => b.low));
  const range    = orbHigh - orbLow;

  // Breakout detection — require price to close at least 0.05% beyond the range
  const breakHigh = price > orbHigh + orbHigh * 0.0005;
  const breakLow  = price < orbLow  - orbLow  * 0.0005;
  const inside    = !breakHigh && !breakLow;
  const direction = breakHigh ? 'up' : breakLow ? 'down' : null;

  const statusColor = breakHigh ? 'var(--up)' : breakLow ? 'var(--down)' : 'var(--faint)';
  const statusLabel = breakHigh ? 'BREAKOUT UP' : breakLow ? 'BREAKOUT DOWN' : 'INSIDE RANGE';

  // Strike calculation
  const w      = spreadWidth(atrPct, price);
  let spread   = null;
  if (breakHigh) {
    const sell = floorStrike(orbLow) - 1;   // one strike below range low
    spread = { type: 'bull_put', sellStrike: sell, buyStrike: sell - w, color: 'var(--up)',
               refLabel: 'ORB Low', refValue: orbLow.toFixed(2) };
  } else if (breakLow) {
    const sell = ceilStrike(orbHigh) + 1;   // one strike above range high
    spread = { type: 'bear_call', sellStrike: sell, buyStrike: sell + w, color: 'var(--down)',
               refLabel: 'ORB High', refValue: orbHigh.toFixed(2) };
  }

  return (
    <Panel title="Strategy A — Opening Range Breakout" chip={<Chip label={statusLabel} color={statusColor} />}>
      {/* Range metrics */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 14 }}>
        <Metric label="ORB High" value={`$${orbHigh.toFixed(2)}`} color="var(--up)" sub="Ceiling" />
        <Metric label="ORB Low"  value={`$${orbLow.toFixed(2)}`}  color="var(--down)" sub="Floor" />
        <Metric label="Range"    value={`$${range.toFixed(2)}`}   color="var(--muted)" sub="Width" />
        <Metric label={`${ticker} Now`}  value={`$${price?.toFixed(2)}`}
          color={breakHigh ? 'var(--up)' : breakLow ? 'var(--down)' : 'var(--text)'}
          sub={breakHigh ? `↑ $${(price - orbHigh).toFixed(2)} above high` : breakLow ? `↓ $${(orbLow - price).toFixed(2)} below low` : 'Inside range'} />
      </div>

      {/* Reading */}
      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.55 }}>
        {inside && `Price is still inside the opening range ($${orbLow.toFixed(2)}–$${orbHigh.toFixed(2)}). No breakout yet — the range is intact. Consider a credit spread (sell premium on one side of the range). Wait for a decisive break before entering a debit spread.`}
        {breakHigh && `Price broke above the ORB high ($${orbHigh.toFixed(2)}) — trend day confirmed. On trend days, BUY a call debit spread. Long strike near current price, short strike ~0.8–1% higher. Place a 50% profit limit order immediately after entry.`}
        {breakLow  && `Price broke below the ORB low ($${orbLow.toFixed(2)}) — trend day confirmed. On trend days, BUY a put debit spread. Long strike near current price, short strike ~0.8–1% lower. Place a 50% profit limit order immediately after entry.`}
      </div>

      {/* Spread suggestion */}
      {spread && (
        <SpreadSuggestion {...spread} />
      )}
      {inside && (
        <div style={{ fontSize: 11, color: 'var(--faint)', textAlign: 'center', padding: '14px 0', fontStyle: 'italic' }}>
          Waiting for breakout — no trade yet.
        </div>
      )}
    </Panel>
  );
}

// ── Strategy B: VWAP + 9 EMA Alignment ───────────────────────────────────────
export function StrategyB({ bars, minutesIn, price, vwap, atrPct, ticker="SPY" }) {
  // Need at least 9 complete 5-min candles for the EMA seed (~45 min)
  const candles5  = aggregateBars(bars, 5);
  const closes5   = candles5.map(c => c.close);
  const ema9      = calcEMA(closes5, 9);
  const ready     = candles5.length >= 9 && ema9 != null;

  if (!ready) {
    const waitMins = Math.max(0, 45 - minutesIn);
    const have5min = candles5.length;
    return (
      <Panel title="Strategy B — VWAP + 9 EMA Alignment" chip={<Chip label="LOADING" color="var(--warn)" />}>
        <div style={{ fontSize: 11, color: 'var(--faint)', lineHeight: 1.7 }}>
          Needs 9 complete 5-minute candles to compute the EMA.
          {waitMins > 0
            ? ` About ${waitMins} min remaining.`
            : have5min > 0
              ? ` Have ${have5min}/9 candles from ${bars.length} price bars — computing…`
              : ` No price bars yet. Make sure the engine is running and this ticker is in your watched symbols list.`
          }
        </div>
      </Panel>
    );
  }

  const last2 = candles5.slice(-2);
  const bothAboveVwap = last2.every(c => c.close > vwap);
  const bothBelowVwap = last2.every(c => c.close < vwap);
  const bothAboveEma  = last2.every(c => c.close > ema9);
  const bothBelowEma  = last2.every(c => c.close < ema9);
  const bullSignal    = bothAboveVwap && bothAboveEma;
  const bearSignal    = bothBelowVwap && bothBelowEma;
  const mixed         = !bullSignal && !bearSignal;

  const statusLabel = bullSignal ? 'BULLISH ALIGNED' : bearSignal ? 'BEARISH ALIGNED' : 'NO SIGNAL';
  const statusColor = bullSignal ? 'var(--up)' : bearSignal ? 'var(--down)' : 'var(--faint)';

  const w     = spreadWidth(atrPct, price);
  let spread  = null;
  if (bullSignal) {
    const sell  = floorStrike(ema9) - 1;  // just below the 9 EMA
    spread = { type: 'bull_put', sellStrike: sell, buyStrike: sell - w, color: 'var(--up)',
               refLabel: '9 EMA (5-min)', refValue: ema9.toFixed(2) };
  } else if (bearSignal) {
    const sell  = ceilStrike(ema9) + 1;   // just above the 9 EMA
    spread = { type: 'bear_call', sellStrike: sell, buyStrike: sell + w, color: 'var(--down)',
               refLabel: '9 EMA (5-min)', refValue: ema9.toFixed(2) };
  }

  // Per-candle check display
  const candleRow = (c, i) => {
    const aboveVwap = c.close > vwap;
    const aboveEma  = c.close > ema9;
    const aligned   = aboveVwap === aboveEma;
    return (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10,
        padding: '5px 10px', borderRadius: 5, background: 'var(--bg)',
        border: `1px solid ${aligned ? (aboveVwap ? 'var(--up)' : 'var(--down)') + '40' : 'var(--border)'}` }}>
        <span style={{ color: 'var(--faint)', fontFamily: 'monospace', minWidth: 52 }}>
          {i === 0 ? 'C-2' : 'C-1 (last)'}
        </span>
        <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--text)', minWidth: 56 }}>
          ${c.close.toFixed(2)}
        </span>
        <span style={{ color: aboveVwap ? 'var(--up)' : 'var(--down)', minWidth: 88 }}>
          {aboveVwap ? '↑ above' : '↓ below'} VWAP
        </span>
        <span style={{ color: aboveEma ? 'var(--up)' : 'var(--down)' }}>
          {aboveEma ? '↑ above' : '↓ below'} 9 EMA
        </span>
        {aligned && <span style={{ marginLeft: 'auto', fontSize: 9, fontWeight: 700, color: aboveVwap ? 'var(--up)' : 'var(--down)' }}>✓ ALIGNED</span>}
      </div>
    );
  };

  return (
    <Panel title="Strategy B — VWAP + 9 EMA Alignment" chip={<Chip label={statusLabel} color={statusColor} />}>
      {/* Key levels */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 14 }}>
        <Metric label="VWAP"        value={`$${vwap?.toFixed(2)}`}  color="var(--warn)" sub="Institutional benchmark" />
        <Metric label="9 EMA (5m)"  value={`$${ema9.toFixed(2)}`}   color="#818cf8"    sub="Trend filter" />
        <Metric label={`${ticker} Now`}     value={`$${price?.toFixed(2)}`}
          color={bullSignal ? 'var(--up)' : bearSignal ? 'var(--down)' : 'var(--text)'}
          sub={price > vwap ? `↑ $${(price - vwap).toFixed(2)} above VWAP` : `↓ $${(vwap - price).toFixed(2)} below VWAP`} />
        <Metric label="5-min candles" value={candles5.length} color="var(--faint)" sub="Session so far" />
      </div>

      {/* Last 2 candle alignment check */}
      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--faint)', marginBottom: 6 }}>
        Last 2 completed 5-min candles
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}>
        {last2.map((c, i) => candleRow(c, i))}
      </div>

      {/* Reading */}
      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.55 }}>
        {bullSignal && `Two candles closed above VWAP ($${vwap?.toFixed(2)}) and 9 EMA ($${ema9.toFixed(2)}). Bullish trend alignment confirmed. On a trend day, BUY a call debit spread near current price. On a slow grind day, SELL a put spread below the 9 EMA. Place a 50% profit limit order immediately after entry.`}
        {bearSignal && `Two candles closed below VWAP ($${vwap?.toFixed(2)}) and 9 EMA ($${ema9.toFixed(2)}). Bearish trend alignment confirmed. On a trend day, BUY a put debit spread near current price. On a slow grind down, SELL a call spread above the 9 EMA. Place a 50% profit limit order immediately after entry.`}
        {mixed && `No alignment yet. Last two 5-min candles are mixed — one or both haven't closed on the same side of VWAP and the 9 EMA simultaneously. Wait for a clean two-candle confirmation before entering.`}
      </div>

      {spread && <SpreadSuggestion {...spread} />}
    </Panel>
  );
}

// ── Combined signal ───────────────────────────────────────────────────────────
function CombinedSignal({ orbDir, emaDir }) {
  if (!orbDir && !emaDir) return null;
  const agree    = orbDir && emaDir && orbDir === emaDir;
  const disagree = orbDir && emaDir && orbDir !== emaDir;
  const oneOnly  = !agree && !disagree && (orbDir || emaDir); // ← must exclude agree
  const dir      = agree ? orbDir : (orbDir || emaDir);
  const color    = dir === 'up' ? 'var(--up)' : 'var(--down)';

  const label = agree    ? `✓✓ BOTH STRATEGIES AGREE — STRONG ${dir === 'up' ? 'BULLISH' : 'BEARISH'} SIGNAL`
              : disagree ? '⚠ STRATEGIES CONFLICT — MIXED DAY, SIT OUT OR REDUCE SIZE'
              :             `ONE SIGNAL — ${dir === 'up' ? 'BULLISH' : 'BEARISH'} (wait for second confirmation)`;
  const body  = agree    ? `Both ORB and VWAP/EMA are pointing ${dir === 'up' ? 'up' : 'down'}. High-conviction — two independent methods agree. Full sizing is appropriate.`
              : disagree ? 'ORB and VWAP/EMA conflict. Day is likely choppy or transitioning. Consider waiting, or trade smaller size on the VWAP/EMA signal only (it updates continuously).'
              :             'Only one strategy has fired. Valid setup but lower conviction. Trade at 50–75% normal size and wait to see if the second strategy confirms.';

  return (
    <div style={{ padding:'12px 14px', borderRadius:7, marginBottom:16,
      border:`1px solid ${agree?color:disagree?'var(--warn)':'var(--border)'}`,
      background:agree?color+'0e':disagree?'var(--warn)0e':'var(--bg)' }}>
      <div style={{ fontSize:10, fontWeight:800, letterSpacing:'.06em',
        color:agree?color:disagree?'var(--warn)':'var(--muted)', marginBottom:5 }}>{label}</div>
      <div style={{ fontSize:11, color:'var(--muted)', lineHeight:1.5 }}>{body}</div>
    </div>
  );
}
export { CombinedSignal };

// ── Panel wrapper ─────────────────────────────────────────────────────────────
export function Panel({ title, chip, children }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px', marginBottom: 14, background: 'var(--bg)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.05em', color: 'var(--text)' }}>
          {title}
        </span>
        {chip}
      </div>
      {children}
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────


// ── SPY Pre-market Direction Panel ────────────────────────────────────────────
// Mirrors GameDayTab's DirectionPanel but hard-coded for SPY.
// Derives BULLISH / BEARISH / WAIT direction from gap, daily ADX, coil.

function getSpyDirection(pm, vixPrice) {
  if (!pm?.ready) return { dir: 'LOADING', color: 'var(--faint)', label: 'Loading pre-market data…', notes: [], vixNote: '' };
  if (pm.coil?.coiling) return {
    dir: 'WAIT', label: 'Wait — price coiling, no breakout yet', color: '#ef4444',
    notes: [
      `SPY has been in a tight $${pm.coil?.bandLow}–$${pm.coil?.bandHigh} range for ${pm.coil?.daysInBand} days.`,
      `Wait for a clean break above $${pm.coil?.bandHigh} or below $${pm.coil?.bandLow} before entering any spread.`,
    ], vixNote: '',
  };

  const signals = [];
  if (pm.gapPct != null) {
    const abs = Math.abs(pm.gapPct);
    if (abs >= 0.8) signals.push({ weight: 12, dir: pm.gapPct > 0 ? 'BULL' : 'BEAR',
      note: `SPY gapped ${pm.gapPct > 0 ? 'up' : 'down'} ${abs.toFixed(2)}% overnight — trend day likely.` });
    else if (abs >= 0.4) signals.push({ weight: 5, dir: pm.gapPct > 0 ? 'BULL' : 'BEAR',
      note: `Small gap ${pm.gapPct > 0 ? 'up' : 'down'} ${abs.toFixed(2)}%.` });
  }
  if (pm.dailyADX >= 20 && pm.priorDir && pm.priorDir !== 'flat')
    signals.push({ weight: pm.dailyADX >= 25 ? 7 : 3, dir: pm.priorDir === 'up' ? 'BULL' : 'BEAR',
      note: `Daily trend ${pm.priorDir === 'up' ? 'up' : 'down'} (ADX ${pm.dailyADX?.toFixed(0)}).` });

  const bull = signals.filter(s => s.dir === 'BULL').reduce((s, x) => s + x.weight, 0);
  const bear = signals.filter(s => s.dir === 'BEAR').reduce((s, x) => s + x.weight, 0);
  const tot  = bull + bear || 1;
  const vixNote = vixPrice != null
    ? vixPrice >= 18
      ? `VIX ${vixPrice.toFixed(1)} elevated — bigger moves expected. Good for debit spread buyers on trend days.`
      : `VIX ${vixPrice.toFixed(1)} low — small moves expected today. Credit spreads have edge.`
    : '';

  if (bull > bear * 1.4) return { dir: 'BULL', label: '↑ SPY leaning BULLISH', color: '#22c55e',
    confidence: Math.round(bull / tot * 100), notes: signals.filter(s => s.dir==='BULL').map(s=>s.note), vixNote };
  if (bear > bull * 1.4) return { dir: 'BEAR', label: '↓ SPY leaning BEARISH', color: '#ef4444',
    confidence: Math.round(bear / tot * 100), notes: signals.filter(s => s.dir==='BEAR').map(s=>s.note), vixNote };
  return { dir: 'UNCLEAR', label: 'No clear SPY direction — wait for open', color: '#f59e0b',
    confidence: 0, notes: ['Mixed or no signals. Let the first 15 minutes classify the day.'], vixNote };
}

function SpyPreMarketPanel({ pm, vixPrice, marketOpen, loading }) {
  const d = getSpyDirection(pm, vixPrice);
  if (loading && !pm) return (
    <div style={{ fontSize:11, color:'var(--faint)', padding:'8px 0', marginBottom:8 }}>⟳ Loading SPY pre-market data…</div>
  );
  if (!pm) return null;

  return (
    <div style={{ border:`2px solid ${d.color}50`, borderRadius:8, padding:'12px 14px', marginBottom:12,
      background: d.color + '0c' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6, flexWrap:'wrap', gap:6 }}>
        <div>
          <div style={{ fontSize:9, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--faint)', marginBottom:3 }}>
            SPY · Pre-market Direction {!marketOpen && '· Market Closed'}
          </div>
          <div style={{ fontSize:18, fontWeight:900, color:d.color, lineHeight:1.1 }}>{d.label}</div>
          {d.confidence > 0 && <div style={{ fontSize:10, color:'var(--faint)', marginTop:2 }}>Signal confidence: {d.confidence}%</div>}
        </div>
        {/* Key pre-market data */}
        {pm.ready && (
          <div style={{ display:'flex', gap:12, fontSize:9, fontFamily:'monospace', color:'var(--faint)', alignItems:'flex-start', flexWrap:'wrap' }}>
            {pm.gapPct != null && <span style={{ color: Math.abs(pm.gapPct)>=0.5 ? d.color : 'var(--faint)' }}>Gap {pm.gapPct>0?'+':''}{pm.gapPct}%</span>}
            {pm.dailyADX != null && <span>ADX {pm.dailyADX} {pm.dailyADX>=25?'(trend)':pm.dailyADX<18?'(range)':''}</span>}
            {vixPrice != null && <span>VIX {vixPrice.toFixed(1)}</span>}
            {pm.priorHigh != null && <span>Prev H ${pm.priorHigh}</span>}
            {pm.priorLow  != null && <span>Prev L ${pm.priorLow}</span>}
          </div>
        )}
      </div>

      {d.notes.length > 0 && (
        <div style={{ display:'flex', flexDirection:'column', gap:3, marginBottom: d.vixNote ? 6 : 0 }}>
          {d.notes.map((n, i) => (
            <div key={i} style={{ fontSize:10, color:'var(--text)', lineHeight:1.5 }}>
              <span style={{ color:d.color, marginRight:5 }}>{i===0?'→':'·'}</span>{n}
            </div>
          ))}
        </div>
      )}
      {d.vixNote && (
        <div style={{ fontSize:10, color:'var(--muted)', lineHeight:1.5, borderTop:'1px solid var(--border)', paddingTop:6, marginTop:4 }}>
          {d.vixNote}
        </div>
      )}
    </div>
  );
}

export function calcExitSignals(pos, price, allInds, vwap, atrPct, alertCfg, ticker="SPY") {
  const signals = [];
  if (!pos.active || !price) return signals;
  const isBull = pos.type === 'bull_put' || pos.type === 'call_debit';
  const isDebit = pos.type === 'call_debit' || pos.type === 'put_debit';
  const shortStr = Number(pos.shortStrike), premium = Number(pos.premium);
  const cfg = alertCfg?.indicators ?? {};

  if (isDebit) {
    // Debit spread: bought premium, profit from price moving in direction
    const debitPnl = estimateDebitPnl(pos, price);
    if (debitPnl) {
      if (debitPnl.pnl < -debitPnl.maxLoss*0.5)
        signals.push({ level:'STOP LOSS', color:'#ef4444',
          text:`Position down ~50% of what you paid ($${Math.abs(debitPnl.pnl).toFixed(0)}). Cut the loss — don\'t let it go to zero.` });
      else if (debitPnl.pctOfMax >= 50)
        signals.push({ level:'TAKE PROFIT', color:'#22c55e',
          text:`Position up ~${debitPnl.pctOfMax.toFixed(0)}% of max profit (+$${debitPnl.pnl.toFixed(0)}). Sell now — 50% of max is the target. Don\'t get greedy.` });
      else if (debitPnl.pctOfMax >= 30)
        signals.push({ level:'CAUTION', color:'#f59e0b',
          text:`Position at ~${debitPnl.pctOfMax.toFixed(0)}% of max profit. Watch indicators — consider selling if momentum turns.` });
    }
  } else {
    // Credit spread: sold premium, profit from time decay / price staying put
    const dist = isBull ? price - shortStr : shortStr - price;
    if (dist < 0)
      signals.push({ level:'STOP LOSS', color:'#ef4444',
        text:`${ticker} $${price.toFixed(2)} breached the short ${isBull?'put':'call'} at $${shortStr}. Max loss — exit immediately.` });
    else if (dist < 1.0)
      signals.push({ level:'DANGER', color:'#f97316',
        text:`${ticker} $${price.toFixed(2)} within $1 of the short strike ($${shortStr}). High risk — consider closing.` });
    if (premium > 0) {
      const buf = premium * 1.5;
      if ((isBull ? price > shortStr+buf : price < shortStr-buf) && dist >= 0)
        signals.push({ level:'TAKE PROFIT', color:'#22c55e',
          text:`Price ~${buf.toFixed(2)} pts beyond your short strike — spread likely near 50% profit. Buy back to lock in.` });
    }
  }

  if (cfg.atrTrail?.enabled && atrPct && price) {
    const atrAbs = (atrPct/100)*price, danger = atrAbs * cfg.atrTrail.multiplier;
    const distAbs = isBull ? shortStr-price : price-shortStr;
    if (distAbs > 0 && distAbs < danger)
      signals.push({ level:'CAUTION', color:'#f59e0b',
        text:`Price within ${cfg.atrTrail.multiplier}\u00d7ATR ($${danger.toFixed(2)}) of short strike — ATR trail stop approaching.` });
  }

  const { macd, maCross, stoch, rsi: rsiVal, vwapCrossed } = allInds;

  if (cfg.macd?.enabled && macd) {
    if (isBull && macd.bearishCross) signals.push({ level:'EXIT SIGNAL', color:'#f59e0b',
      text:`MACD bearish cross (${cfg.macd.timeframeMins}-min, ${cfg.macd.fast}/${cfg.macd.slow}/${cfg.macd.signal}) — bullish momentum fading. Exit bull put spread.` });
    if (!isBull && macd.bullishCross) signals.push({ level:'EXIT SIGNAL', color:'#f59e0b',
      text:`MACD bullish cross (${cfg.macd.timeframeMins}-min) — bearish momentum fading. Exit bear call spread.` });
    if (!macd.bearishCross && !macd.bullishCross) {
      if (isBull && macd.histogram < -0.04) signals.push({ level:'CAUTION', color:'#f59e0b',
        text:`MACD histogram ${macd.histogram.toFixed(3)} — bearish momentum building.` });
      if (!isBull && macd.histogram > 0.04) signals.push({ level:'CAUTION', color:'#f59e0b',
        text:`MACD histogram ${macd.histogram.toFixed(3)} — bullish momentum building.` });
    }
  }

  if (cfg.maCross?.enabled && maCross) {
    if (isBull && maCross.bearishCross) signals.push({ level:'EXIT SIGNAL', color:'#f97316',
      text:`${cfg.maCross.fastPeriod}/${cfg.maCross.slowPeriod} EMA bearish cross (${cfg.maCross.timeframeMins}-min) — trend reversed bearish. Exit bull put spread.` });
    if (!isBull && maCross.bullishCross) signals.push({ level:'EXIT SIGNAL', color:'#f97316',
      text:`${cfg.maCross.fastPeriod}/${cfg.maCross.slowPeriod} EMA bullish cross (${cfg.maCross.timeframeMins}-min) — trend reversed bullish. Exit bear call spread.` });
  }

  if (cfg.stochastic?.enabled && stoch) {
    const ob=cfg.stochastic.overbought, os=cfg.stochastic.oversold;
    if (isBull && stoch.bearishCross && stoch.k > ob)
      signals.push({ level:'EXIT SIGNAL', color:'#f59e0b', text:`Stochastic %K (${stoch.k.toFixed(0)}) overbought \u2192 crossed below %D. Exit or reduce.` });
    if (!isBull && stoch.bullishCross && stoch.k < os)
      signals.push({ level:'EXIT SIGNAL', color:'#f59e0b', text:`Stochastic %K (${stoch.k.toFixed(0)}) oversold \u2192 crossed above %D. Exit or reduce.` });
    if (isBull && stoch.k > ob && (stoch.prevK??0) <= ob)
      signals.push({ level:'CAUTION', color:'#f59e0b', text:`Stochastic %K entered overbought zone (${stoch.k.toFixed(0)}) — watch for reversal.` });
    if (!isBull && stoch.k < os && (stoch.prevK??100) >= os)
      signals.push({ level:'CAUTION', color:'#f59e0b', text:`Stochastic %K entered oversold zone (${stoch.k.toFixed(0)}) — watch for bounce.` });
  }

  if (cfg.rsiExit?.enabled && rsiVal != null) {
    const nz = cfg.rsiExit.neutralZone ?? 50;
    if (isBull && rsiVal < nz) signals.push({ level:'CAUTION', color:'#f59e0b',
      text:`RSI ${rsiVal.toFixed(1)} below ${nz} — bullish momentum losing ground.` });
    if (!isBull && rsiVal > (100-nz)) signals.push({ level:'CAUTION', color:'#f59e0b',
      text:`RSI ${rsiVal.toFixed(1)} above ${100-nz} — bearish momentum weakening.` });
  }

  if (cfg.vwapCross?.enabled && vwapCrossed != null) {
    if (isBull && vwapCrossed === 'below') signals.push({ level:'EXIT SIGNAL', color:'#f97316',
      text:`${ticker} crossed below VWAP ($${vwap?.toFixed(2)}) — institutions flipped bearish. Exit the bull put spread.` });
    if (!isBull && vwapCrossed === 'above') signals.push({ level:'EXIT SIGNAL', color:'#f97316',
      text:`${ticker} crossed above VWAP ($${vwap?.toFixed(2)}) — institutions flipped bullish. Exit the bear call spread.` });
  }

  const seen = new Set();
  return signals.filter(s => { const k=s.level+s.text.slice(0,30); if(seen.has(k)) return false; seen.add(k); return true; });
}

// ── Position entry form ───────────────────────────────────────────────────────
export function PositionForm({ pos, onChange, suggestedType, suggestedSell, suggestedBuy }) {
  const isBull  = pos.type === 'bull_put' || pos.type === 'call_debit';
  const isDebit = pos.type === 'call_debit' || pos.type === 'put_debit';
  const prem    = Number(pos.premium)||0;
  const width   = Math.abs(Number(pos.shortStrike)-Number(pos.longStrike))||0;
  const ctrs    = Number(pos.contracts)||1;
  // Credit spread: collected premium = max profit; debit spread: paid premium = max loss
  const maxProfit = pos.active && prem && ctrs
    ? isDebit ? ((width-prem)*100*ctrs).toFixed(0) : (prem*100*ctrs).toFixed(0)
    : null;
  const maxLoss = pos.active && prem && pos.longStrike && pos.shortStrike && ctrs
    ? isDebit ? (prem*100*ctrs).toFixed(0) : ((width-prem)*100*ctrs).toFixed(0)
    : null;

  return (
    <div style={{ border:'1px solid var(--border)', borderRadius:8, padding:'14px 16px', marginBottom:14 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <span style={{ fontSize:11, fontWeight:800, letterSpacing:'.05em', color:'var(--text)' }}>
          My Position
        </span>
        {pos.active
          ? <button onClick={() => onChange({ ...pos, active: false })}
              style={smallBtn}>✕ Clear</button>
          : suggestedType && (
              <button onClick={() => onChange({ ...pos, type: suggestedType,
                shortStrike: suggestedSell ?? '', longStrike: suggestedBuy ?? '', active: false })}
                style={{ ...smallBtn, color:'var(--accent)', borderColor:'var(--accent)' }}>
                ↓ Use suggested strikes
              </button>
            )
        }
      </div>

      {!pos.active ? (
        <div style={{ display:'grid', gap:10 }}>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            {/* Spread type */}
            <div>
              <div style={fLabel}>Spread type</div>
              <select value={pos.type} onChange={e => onChange({ ...pos, type: e.target.value })} style={fInput}>
                <option value="">Select…</option>
                <optgroup label="Credit Spreads — Selling Premium">
                  <option value="bull_put">Sell Bull Put Spread</option>
                  <option value="bear_call">Sell Bear Call Spread</option>
                </optgroup>
                <optgroup label="Debit Spreads — Buying Direction">
                  <option value="call_debit">Buy Bull Call Spread</option>
                  <option value="put_debit">Buy Bear Put Spread</option>
                </optgroup>
              </select>
            </div>
            {/* Short strike */}
            <div>
              <div style={fLabel}>Short strike (sell)</div>
              <input type="number" placeholder="e.g. 525" value={pos.shortStrike}
                onChange={e => onChange({ ...pos, shortStrike: e.target.value })} style={{ ...fInput, width:90 }} />
            </div>
            {/* Long strike */}
            <div>
              <div style={fLabel}>Long strike (buy)</div>
              <input type="number" placeholder="e.g. 520" value={pos.longStrike}
                onChange={e => onChange({ ...pos, longStrike: e.target.value })} style={{ ...fInput, width:90 }} />
            </div>
            {/* Premium */}
            <div>
              <div style={fLabel}>Credit received ($)</div>
              <input type="number" step="0.01" placeholder="e.g. 1.20" value={pos.premium}
                onChange={e => onChange({ ...pos, premium: e.target.value })} style={{ ...fInput, width:90 }} />
            </div>
            {/* Contracts */}
            <div>
              <div style={fLabel}>Contracts</div>
              <input type="number" min={1} placeholder="1" value={pos.contracts}
                onChange={e => onChange({ ...pos, contracts: e.target.value })} style={{ ...fInput, width:70 }} />
            </div>
          </div>
          <button
            disabled={!pos.type || !pos.shortStrike || !pos.longStrike || !pos.premium}
            onClick={() => onChange({ ...pos, active: true })}
            style={{ alignSelf:'flex-start', padding:'8px 18px', borderRadius:5, border:'none',
              background: pos.type && pos.shortStrike && pos.premium ? 'var(--accent)' : 'var(--border)',
              color: pos.type && pos.shortStrike && pos.premium ? 'var(--bg)' : 'var(--faint)',
              fontWeight:700, fontSize:12, cursor:'pointer' }}>
            Track this position
          </button>
        </div>
      ) : (
        <div style={{ display:'flex', flexWrap:'wrap', gap:20 }}>
          <Metric label="Spread" color={isBull ? 'var(--up)' : 'var(--down)'}
            value={isBull ? 'Sell Bull Put' : 'Sell Bear Call'}
            sub={`$${pos.shortStrike} / $${pos.longStrike}`} />
          <Metric label="Credit received" color="var(--up)"
            value={`$${Number(pos.premium).toFixed(2)}`}
            sub={`per share / $${(Number(pos.premium)*100).toFixed(0)} per contract`} />
          {maxProfit && <Metric label="Max profit" color="var(--up)"
            value={`$${maxProfit}`} sub={`${pos.contracts} contract${pos.contracts>1?'s':''}`} />}
          {maxLoss && <Metric label="Max loss" color="var(--down)"
            value={`$${maxLoss}`} sub={isDebit ? "if spread expires worthless" : "if spread expires ITM"} />}
          <Metric label="50% profit target" color="var(--warn)"
            value={maxProfit ? `$${(maxProfit*0.5).toFixed(0)}` : '—'}
            sub={isDebit ? `Sell when up $${(maxProfit*0.5||0).toFixed(0)}` : `Buy back at $${(prem*0.5).toFixed(2)}`} />
        </div>
      )}
    </div>
  );
}

// ── MACD indicator panel ──────────────────────────────────────────────────────
export function MACDPanel({ macd, posType }) {
  if (!macd) return (
    <Panel title="MACD (5-min, 12/26/9)" chip={<Chip label="LOADING" color="var(--warn)" />}>
      <div style={{ fontSize:11, color:'var(--faint)', lineHeight:1.5 }}>
        Needs 34 five-minute candles (~2h50min into the session) to compute.
        MACD will appear from ~12:20 ET onwards.
      </div>
    </Panel>
  );

  const isBull   = posType === 'bull_put' || posType === 'call_debit';
  const histColor = macd.histogram >= 0 ? 'var(--up)' : 'var(--down)';
  const crossStatus = macd.bearishCross ? 'BEARISH CROSS' : macd.bullishCross ? 'BULLISH CROSS'
    : macd.histogram >= 0 ? 'BULLISH' : 'BEARISH';
  const crossColor = macd.bearishCross ? 'var(--down)' : macd.bullishCross ? 'var(--up)'
    : macd.histogram >= 0 ? 'var(--up)' : 'var(--down)';

  const isBad = posType && ((isBull && macd.histogram < 0) || (!isBull && macd.histogram > 0));

  return (
    <Panel title="MACD (5-min, 12/26/9)" chip={<Chip label={crossStatus} color={crossColor} />}>
      <div style={{ display:'flex', gap:24, flexWrap:'wrap', marginBottom:12 }}>
        <Metric label="MACD line"   value={macd.macd.toFixed(3)}      color={macd.macd >= 0 ? 'var(--up)' : 'var(--down)'} />
        <Metric label="Signal line" value={macd.signal.toFixed(3)}    color="var(--muted)" />
        <Metric label="Histogram"   value={macd.histogram.toFixed(3)} color={histColor}
          sub={macd.histogram >= 0 ? 'Bullish momentum' : 'Bearish momentum'} />
      </div>
      {/* Histogram bar */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
        <span style={{ fontSize:9, color:'var(--faint)', width:52 }}>Momentum</span>
        <div style={{ flex:1, height:8, background:'var(--border)', borderRadius:4, overflow:'hidden', position:'relative' }}>
          <div style={{ position:'absolute', left:'50%', top:0, width:1, height:'100%', background:'var(--faint)' }} />
          {macd.histogram >= 0
            ? <div style={{ position:'absolute', left:'50%', width:`${Math.min(50, Math.abs(macd.histogram)*200)}%`, height:'100%', background:'var(--up)', borderRadius:'0 4px 4px 0' }} />
            : <div style={{ position:'absolute', right:'50%', width:`${Math.min(50, Math.abs(macd.histogram)*200)}%`, height:'100%', background:'var(--down)', borderRadius:'4px 0 0 4px' }} />
          }
        </div>
      </div>
      <div style={{ fontSize:11, color: isBad ? 'var(--down)' : 'var(--muted)', lineHeight:1.5 }}>
        {macd.bearishCross && '⚠ MACD just crossed below its signal line — bearish momentum shift. For bull put spreads: consider exiting.'}
        {macd.bullishCross && '⚠ MACD just crossed above its signal line — bullish momentum shift. For bear call spreads: consider exiting.'}
        {!macd.bearishCross && !macd.bullishCross && (
          macd.histogram >= 0
            ? 'Histogram is positive — buyers have more momentum than sellers. Favourable for bull put spreads.'
            : 'Histogram is negative — sellers have more momentum than buyers. Favourable for bear call spreads.'
        )}
      </div>
    </Panel>
  );
}

// ── MA crossover panel ────────────────────────────────────────────────────────
export function MACrossPanel({ maCross, posType }) {
  if (!maCross) return (
    <Panel title="MA Crossover (9 EMA / 20 EMA, 5-min)" chip={<Chip label="LOADING" color="var(--warn)" />}>
      <div style={{ fontSize:11, color:'var(--faint)' }}>
        Needs 20 five-minute candles (~100 minutes) to compute. Available from ~11:10 ET.
      </div>
    </Panel>
  );

  const statusLabel = maCross.bearishCross ? 'BEARISH CROSS ↓' : maCross.bullishCross ? 'BULLISH CROSS ↑'
    : maCross.bullish ? '9 EMA ABOVE 20' : '9 EMA BELOW 20';
  const statusColor = maCross.bearishCross ? 'var(--down)' : maCross.bullishCross ? 'var(--up)'
    : maCross.bullish ? 'var(--up)' : 'var(--down)';
  const isBull = posType === 'bull_put' || posType === 'call_debit';
  const isBad  = posType && ((isBull && !maCross.bullish) || (!isBull && maCross.bullish));

  return (
    <Panel title="MA Crossover (9 EMA / 20 EMA, 5-min)" chip={<Chip label={statusLabel} color={statusColor} />}>
      <div style={{ display:'flex', gap:24, flexWrap:'wrap', marginBottom:12 }}>
        <Metric label="9 EMA"  value={`$${maCross.ema9.toFixed(2)}`}  color="#818cf8" sub="Fast line" />
        <Metric label="20 EMA" value={`$${maCross.ema20.toFixed(2)}`} color="#fb923c" sub="Slow line" />
        <Metric label="Gap"
          value={`${maCross.bullish ? '+' : '-'}$${Math.abs(maCross.ema9 - maCross.ema20).toFixed(2)}`}
          color={maCross.bullish ? 'var(--up)' : 'var(--down)'}
          sub={maCross.bullish ? '9 above 20 → bullish' : '9 below 20 → bearish'} />
      </div>
      <div style={{ fontSize:11, color: isBad ? 'var(--down)' : 'var(--muted)', lineHeight:1.5 }}>
        {maCross.bearishCross && '⚠ 9 EMA just crossed BELOW 20 EMA — the short-term trend turned bearish. Exit bull put spreads now.'}
        {maCross.bullishCross && '⚠ 9 EMA just crossed ABOVE 20 EMA — the short-term trend turned bullish. Exit bear call spreads now.'}
        {!maCross.bearishCross && !maCross.bullishCross && (
          maCross.bullish
            ? '9 EMA is above 20 EMA — short-term trend is bullish. Supports holding bull put spreads.'
            : '9 EMA is below 20 EMA — short-term trend is bearish. Supports holding bear call spreads.'
        )}
      </div>
    </Panel>
  );
}

// ── Exit signal summary ───────────────────────────────────────────────────────
export function ExitSignalSummary({ signals, pos }) {
  if (!pos.active) return null;
  const highest = signals.find(s => s.level === 'STOP LOSS')
    ?? signals.find(s => s.level === 'TAKE PROFIT')
    ?? signals.find(s => s.level === 'EXIT SIGNAL')
    ?? signals.find(s => s.level === 'DANGER')
    ?? signals.find(s => s.level === 'CAUTION')
    ?? null;

  if (!highest && signals.length === 0) {
    return (
      <div style={{ padding:'12px 16px', borderRadius:8, border:'1px solid var(--up)', background:'var(--up)0e', marginBottom:14 }}>
        <span style={{ fontSize:12, fontWeight:700, color:'var(--up)' }}>✓ Position looks fine</span>
        <span style={{ fontSize:11, color:'var(--muted)', marginLeft:10 }}>
          No exit signals. Continue monitoring.
        </span>
      </div>
    );
  }

  return (
    <div style={{ marginBottom:14 }}>
      {signals.map((sig, i) => (
        <div key={i} style={{ padding:'12px 16px', borderRadius:8, marginBottom:6,
          border:`1px solid ${sig.color}`, background:`${sig.color}12`,
          display:'flex', alignItems:'flex-start', gap:10 }}>
          <span style={{ fontSize:9, fontWeight:800, letterSpacing:'.08em', padding:'2px 7px',
            borderRadius:3, background:sig.color, color:'var(--bg)', flexShrink:0, marginTop:1 }}>
            {sig.level}
          </span>
          <span style={{ fontSize:11, color:'var(--text)', lineHeight:1.55 }}>{sig.text}</span>
        </div>
      ))}
    </div>
  );
}

// ── Safe Day alert settings panel ─────────────────────────────────────────────
const RINGTONE_ID = 'safeday'; // reuses tier ringtone KV endpoint

function SafeDayAlertSettings({ settings, onSave }) {
  const [draft,    setDraft]    = useState(() => mergeDefaults(settings));
  const [dirty,    setDirty]    = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [ringtone, setRingtone] = useState({ url: null, name: '' });
  const [uploading,setUploading]= useState(false);
  const [notifPerm,setNotifPerm]= useState(Notification?.permission ?? 'default');

  // Load saved ringtone on mount
  useEffect(() => {
    tierGetRingtone(RINGTONE_ID).then(url => { if (url) setRingtone({ url, name: 'Custom tone' }); }).catch(() => {});
  }, []);

  const upd = (k, v) => { setDraft(d => ({ ...d, [k]: v })); setDirty(true); };
  const updType = (key, k, v) => {
    setDraft(d => ({ ...d, alertTypes: { ...d.alertTypes, [key]: { ...d.alertTypes[key], [k]: v } } }));
    setDirty(true);
  };

  const doSave = async () => {
    setSaving(true);
    try { await onSave(draft); setDirty(false); }
    catch (e) { alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  };

  const handleFile = async (file) => {
    if (!file?.type.startsWith('audio/')) { alert('Select an audio file'); return; }
    if (file.size > 3_500_000) { alert('File must be under 3.5 MB'); return; }
    setUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
      });
      await tierPutRingtone(RINGTONE_ID, dataUrl);
      setRingtone({ url: dataUrl, name: file.name });
    } catch (e) { alert('Upload failed: ' + e.message); }
    finally { setUploading(false); }
  };

  const deleteRingtone = async () => {
    await tierDeleteRingtone(RINGTONE_ID);
    setRingtone({ url: null, name: '' });
  };

  const testAudio = () => {
    if (ringtone.url) {
      const a = new Audio(ringtone.url);
      playAudio(a, { targetVol: draft.volume, cutoffSecs: draft.ringtoneCutoffSecs, gradual: draft.gradualVolume });
    } else {
      playBeep('bullish', draft.volume);
    }
  };

  const requestNotif = async () => {
    const result = await Notification.requestPermission();
    setNotifPerm(result);
    if (result === 'granted') upd('browserNotif', true);
  };

  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 6 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.06em', color: 'var(--text)', marginBottom: 12 }}>
        ALERT SETTINGS
      </div>

      {/* Master enable */}
      <div style={{ ...sCard2, display: 'flex', alignItems: 'center', gap: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          <input type="checkbox" checked={draft.enabled} onChange={e => upd('enabled', e.target.checked)}
            style={{ accentColor: 'var(--accent)', width: 15, height: 15 }} />
          <span style={{ color: 'var(--text)' }}>Safe Day alerts enabled</span>
        </label>
        <span style={{ fontSize: 11, color: 'var(--faint)' }}>
          — fires audio + notifications when exit signals occur while a position is active
        </span>
      </div>

      {/* Per-alert-type toggles */}
      <div style={sCard2}>
        <div style={sCardTitle}>Alert Types</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {Object.entries(draft.alertTypes).map(([key, t]) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
              padding: '6px 10px', borderRadius: 6, border: `1px solid ${t.enabled ? t.color + '50' : 'var(--border)'}`,
              background: t.enabled ? t.color + '0c' : 'transparent', transition: 'all .15s' }}>
              <input type="checkbox" checked={t.enabled} onChange={e => updType(key, 'enabled', e.target.checked)}
                style={{ accentColor: t.color, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: t.enabled ? t.color : 'var(--faint)' }}>{t.label}</div>
                <div style={{ fontSize: 9, color: 'var(--faint)' }}>
                  {key === 'STOP_LOSS'   && 'Price breaches short strike'}
                  {key === 'DANGER'      && 'Within $1 of short strike'}
                  {key === 'EXIT_SIGNAL' && 'MACD or MA crossover against position'}
                  {key === 'TAKE_PROFIT' && '~50% profit level reached'}
                  {key === 'CAUTION'     && 'MACD histogram building against position'}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Indicator configuration */}
      <div style={sCard2}>
        <div style={sCardTitle}>Indicator Configuration</div>
        <div style={{ fontSize:11, color:'var(--faint)', marginBottom:14, lineHeight:1.5 }}>
          Each indicator fires exit/caution signals when active and a position is tracked.
          Adjust timeframe and parameters to control how quickly and how strictly each fires.
          Shorter timeframe = fires sooner but noisier. Longer = slower but more reliable.
        </div>

        {[
          { key:'macd', label:'MACD', color:'#818cf8',
            desc: (c) => `(${c.fast}/${c.slow}/${c.signal}) on ${c.timeframeMins}-min — fires after ~${(c.slow+c.signal-1)*c.timeframeMins} min`,
            fields: [
              { label:'Timeframe (min)', k:'timeframeMins', min:1, max:5, step:1 },
              { label:'Fast period', k:'fast', min:3, max:20, step:1 },
              { label:'Slow period', k:'slow', min:10, max:50, step:1 },
              { label:'Signal period', k:'signal', min:3, max:15, step:1 },
            ]},
          { key:'maCross', label:'MA Crossover (EMA)', color:'#fb923c',
            desc: (c) => `${c.fastPeriod}/${c.slowPeriod} EMA on ${c.timeframeMins}-min — fires after ~${c.slowPeriod*c.timeframeMins} min`,
            fields: [
              { label:'Timeframe (min)', k:'timeframeMins', min:1, max:5, step:1 },
              { label:'Fast EMA period', k:'fastPeriod', min:3, max:20, step:1 },
              { label:'Slow EMA period', k:'slowPeriod', min:10, max:50, step:1 },
            ]},
          { key:'stochastic', label:'Stochastic %K/%D', color:'#34d399',
            desc: (c) => `K=${c.kPeriod} D=${c.dPeriod} on ${c.timeframeMins}-min · OB/OS ${c.overbought}/${c.oversold} — fires after ~${(c.kPeriod+c.dPeriod-1)*c.timeframeMins} min`,
            fields: [
              { label:'Timeframe (min)', k:'timeframeMins', min:1, max:5, step:1 },
              { label:'%K period', k:'kPeriod', min:5, max:21, step:1 },
              { label:'%D smoothing', k:'dPeriod', min:1, max:5, step:1 },
              { label:'Overbought threshold', k:'overbought', min:60, max:95, step:5 },
              { label:'Oversold threshold', k:'oversold', min:5, max:40, step:5 },
            ]},
          { key:'rsiExit', label:'RSI Exit', color:'#f472b6',
            desc: (c) => `Period ${c.period} on ${c.timeframeMins}-min · neutral zone ${c.neutralZone} — fires after ~${(c.period+1)*c.timeframeMins} min`,
            fields: [
              { label:'Timeframe (min)', k:'timeframeMins', min:1, max:5, step:1 },
              { label:'RSI period', k:'period', min:7, max:21, step:1 },
              { label:'Neutral zone (exit bull when below)', k:'neutralZone', min:40, max:60, step:1, note:'— lower = stricter for bulls' },
            ]},
          { key:'vwapCross', label:'VWAP Cross', color:'#f59e0b',
            desc: () => 'Fires when price crosses VWAP (no timeframe — always on)',
            fields: [
              { label:'Band tolerance (%)', k:'bandPct', min:0, max:0.2, step:0.01, decimals:2 },
            ]},
          { key:'atrTrail', label:'ATR Trail Stop', color:'#ef4444',
            desc: (c) => `Alerts when price is within ${c.multiplier}×ATR of short strike`,
            fields: [
              { label:'ATR multiplier', k:'multiplier', min:0.5, max:5, step:0.5, decimals:1 },
            ]},
        ].map(({ key, label, color, desc, fields }) => {
          const indDraft = draft.indicators?.[key] ?? {};
          const updInd = (k, v) => {
            setDraft(d => ({ ...d, indicators: { ...(d.indicators??{}), [key]: { ...(d.indicators?.[key]??{}), [k]: v } } }));
            setDirty(true);
          };
          const isEnabled = indDraft.enabled ?? true;
          return (
            <div key={key} style={{ marginBottom:14, padding:'10px 14px', borderRadius:7,
              border:`1px solid ${isEnabled ? color+'50' : 'var(--border)'}`,
              background: isEnabled ? color+'0a' : 'transparent' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                    <input type="checkbox" checked={isEnabled}
                      onChange={e => updInd('enabled', e.target.checked)}
                      style={{ accentColor: color }} />
                    <span style={{ fontSize:12, fontWeight:700, color: isEnabled ? color : 'var(--faint)' }}>{label}</span>
                  </label>
                </div>
                <span style={{ fontSize:9, color:'var(--faint)', fontFamily:'monospace', textAlign:'right' }}>
                  {desc(indDraft)}
                </span>
              </div>
              {isEnabled && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'2px 16px', marginTop:8 }}>
                  {fields.map(f => (
                    <Slider key={f.k} label={f.label} value={indDraft[f.k] ?? 0}
                      min={f.min} max={f.max} step={f.step}
                      unit={f.decimals ? '' : ''}
                      note={f.note}
                      onChange={v => updInd(f.k, f.decimals ? Number(v.toFixed(f.decimals)) : v)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Audio */}
      <div style={sCard2}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={sCardTitle}>Audio</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12 }}>
            <input type="checkbox" checked={draft.audioEnabled} onChange={e => upd('audioEnabled', e.target.checked)} />
            Enabled
          </label>
        </div>
        <Slider label="Volume" value={Math.round(draft.volume * 100)} unit="%" min={0} max={100} step={5}
          onChange={v => upd('volume', v / 100)} />
        <Slider label="Alert cooldown" value={draft.cooldownMins} unit=" min" min={1} max={30} step={1}
          onChange={v => upd('cooldownMins', v)} note="— min gap between same alert type" />
        <Slider label="Ringtone cutoff" value={draft.ringtoneCutoffSecs} min={0} max={30} step={1}
          onChange={v => upd('ringtoneCutoffSecs', v)}
          note={draft.ringtoneCutoffSecs === 0 ? '— plays full clip' : `— stops after ${draft.ringtoneCutoffSecs}s`} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, marginTop: 8, marginBottom: 12 }}>
          <input type="checkbox" checked={draft.gradualVolume} onChange={e => upd('gradualVolume', e.target.checked)} />
          <div>
            <div style={{ color: 'var(--text)', fontWeight: 500 }}>Gradually increase volume</div>
            <div style={{ fontSize: 10, color: 'var(--faint)' }}>
              Ramps 30% → 130% over the cutoff window.
              {draft.ringtoneCutoffSecs === 0 && draft.gradualVolume && <span style={{ color: 'var(--warn)', marginLeft: 4 }}>Set a cutoff time to enable.</span>}
            </div>
          </div>
        </label>
        {/* Ringtone upload */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', marginBottom: 6 }}>Ringtone</div>
          <div style={{ fontSize: 11, color: 'var(--faint)', marginBottom: 10 }}>
            mp3 / wav / ogg, max 3.5 MB. Saved to your account, loads on any device.
          </div>
          {ringtone.url ? (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, background: 'var(--up-soft)', color: 'var(--up)', fontWeight: 600 }}>
                🎵 {ringtone.name || 'Custom tone'}
              </span>
              <button onClick={testAudio} style={sdBtn}>▶ Test</button>
              <label style={{ ...sdBtn, display: 'inline-block', cursor: 'pointer' }}>
                Replace<input type="file" accept="audio/*" style={{ display: 'none' }}
                  onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); e.target.value = ''; }} />
              </label>
              <button onClick={deleteRingtone} style={{ ...sdBtn, color: 'var(--down)' }}>Remove</button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ ...sdBtn, display: 'inline-block', cursor: 'pointer', opacity: uploading ? 0.6 : 1 }}>
                {uploading ? 'Uploading…' : '↑ Upload audio'}
                <input type="file" accept="audio/*" style={{ display: 'none' }}
                  onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); e.target.value = ''; }} />
              </label>
              <button onClick={testAudio} style={sdBtn}>▶ Test beep</button>
              <span style={{ fontSize: 11, color: 'var(--faint)' }}>falls back to built-in beep</span>
            </div>
          )}
        </div>
      </div>

      {/* Browser notifications */}
      <div style={sCard2}>
        <div style={sCardTitle}>Browser Notifications</div>
        <div style={{ fontSize: 11, color: 'var(--faint)', marginBottom: 10 }}>
          Desktop pop-up even when the tab is backgrounded.
        </div>
        {notifPerm === 'granted' ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12 }}>
            <input type="checkbox" checked={draft.browserNotif} onChange={e => upd('browserNotif', e.target.checked)} />
            Send notification on Safe Day alerts
          </label>
        ) : notifPerm === 'denied' ? (
          <div style={{ fontSize: 11, color: 'var(--down)' }}>Blocked — reset in browser site settings.</div>
        ) : (
          <button onClick={requestNotif} style={sdBtn}>Enable browser notifications</button>
        )}
      </div>

      {/* Save bar */}
      {dirty && (
        <div style={{ position: 'sticky', bottom: 16, display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', padding: '10px 14px', borderRadius: 6,
          border: '1px solid var(--border)', background: 'var(--bg)', boxShadow: '0 4px 16px rgba(0,0,0,.3)' }}>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Unsaved changes</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { setDraft(mergeDefaults(settings)); setDirty(false); }} style={sdBtn}>Discard</button>
            <button onClick={doSave} disabled={saving}
              style={{ ...sdBtn, background: 'var(--accent)', color: 'var(--bg)', borderColor: 'var(--accent)', fontWeight: 700 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function mergeDefaults(s) {
  const d = DEFAULT_SAFE_DAY_ALERT;
  if (!s) return JSON.parse(JSON.stringify(d)); // deep copy
  return {
    ...d, ...s,
    alertTypes: Object.fromEntries(
      Object.entries(d.alertTypes).map(([k, def]) => [k, { ...def, ...(s.alertTypes?.[k] ?? {}) }])
    ),
    indicators: Object.fromEntries(
      Object.entries(d.indicators).map(([k, def]) => [k, { ...def, ...(s.indicators?.[k] ?? {}) }])
    ),
  };
}

function Slider({ label, value, unit='', min, max, step, onChange, note }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--text)' }}>
          {value}{unit}{note && <span style={{ fontWeight: 400, color: 'var(--faint)', marginLeft: 4 }}>{note}</span>}
        </span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: '100%', accentColor: 'var(--accent)' }} />
    </div>
  );
}

const sCard2    = { padding: '12px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)', marginBottom: 10 };
const sCardTitle = { fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 };
const sdBtn     = { fontSize: 10, padding: '4px 10px', border: '1px solid var(--border)', borderRadius: 4, background: 'transparent', color: 'var(--muted)', cursor: 'pointer' };

export default function SafeDayTab({ engineOn, liveState, marketOpen, globalSettings, saveGlobalSettings, vixQuote }) {
  const bars      = liveState?.bars ?? [];
  const minutesIn = liveState?.minutesIntoSession ?? 0;
  const price     = liveState?.price;
  const vwap      = liveState?.vwap;
  const atrPct    = liveState?.atrPct;
  const vixPrice  = vixQuote?.regularMarketPrice ?? vixQuote?.preMarketPrice ?? null;

  const [showSettings, setShowSettings] = useState(false);
  const instrument = 'SPX'; // Safe Day is SPX-only
  const toggleInstrument = () => {
    const next = instrument === 'SPY' ? 'SPX' : 'SPY';
    localStorage.setItem('sd:instrument', next);                        // instant local update
    saveGlobalSettings?.({ ...globalSettings, spyMode: next });        // persist to KV
  };

  // ── Pre-market bootstrap — SPY daily bars for direction panel ─────────────
  const [spyPM, setSpyPM]         = useState(null);
  const [pmLoading, setPmLoading] = useState(false);
  const pmFetchedRef = useRef(false);
  useEffect(() => {
    if (pmFetchedRef.current) return;
    pmFetchedRef.current = true;
    setPmLoading(true);
    const h = { 'x-session-token': getToken() };
    Promise.all([
      fetch(`${BASE}/chart?symbol=SPY&interval=1d&range=2mo`, { headers: h }).then(r => r.ok ? r.json() : null),
      fetch(`${BASE}/quote?symbols=SPY`, { headers: h }).then(r => r.ok ? r.json() : null),
    ]).then(([chartData, quoteData]) => {
      let dailyBars = [];
      if (chartData?.bars?.length) dailyBars = chartData.bars.filter(b => b.c != null)
        .map(b => ({ time: b.t / 1000, open: b.o, high: b.h, low: b.l, close: b.c, volume: b.v ?? 0 }));
      const q = quoteData?.SPY ?? quoteData?.quoteResponse?.result?.[0] ?? null;
      const preMarketPrice = q?.preMarketPrice ?? q?.regularMarketPrice ?? null;
      setSpyPM(analyzePreMarket(dailyBars, preMarketPrice));
    }).catch(() => {}).finally(() => setPmLoading(false));
  }, []);

  // Use bootstrap pre-market OR liveState.preMarket (whichever is available)
  const pm = spyPM ?? liveState?.preMarket ?? null;

  // Alert settings
  const alertCfg    = mergeDefaults(globalSettings?.safeDayAlerts);
  const ringtoneRef = useRef(null);
  useEffect(() => {
    tierGetRingtone(RINGTONE_ID).then(url => { if (url) ringtoneRef.current = new Audio(url); }).catch(() => {});
  }, []);
  const saveAlertSettings = useCallback(async (draft) => {
    await saveGlobalSettings?.({ ...globalSettings, safeDayAlerts: draft });
  }, [globalSettings, saveGlobalSettings]);

  // Position state
  const [pos, setPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sd:position') || 'null') ?? { type:'', shortStrike:'', longStrike:'', premium:'', contracts:1, active:false }; }
    catch { return { type:'', shortStrike:'', longStrike:'', premium:'', contracts:1, active:false }; }
  });
  const handlePosChange = (next) => { setPos(next); localStorage.setItem('sd:position', JSON.stringify(next)); };

  // Indicators — all configurable via alertCfg.indicators
  const indCfg  = alertCfg.indicators ?? {};
  const macdCfg = indCfg.macd      ?? {};
  const maCfg   = indCfg.maCross   ?? {};
  const stochCfg= indCfg.stochastic ?? {};
  const rsiCfg  = indCfg.rsiExit   ?? {};
  const bbCfg   = indCfg.bbWidth   ?? {};

  const barsForMACD   = aggregateBars(bars, macdCfg.timeframeMins ?? 1);
  const barsForMA     = aggregateBars(bars, maCfg.timeframeMins   ?? 5);
  const barsForStoch  = aggregateBars(bars, stochCfg.timeframeMins?? 1);
  const barsForRSI    = aggregateBars(bars, rsiCfg.timeframeMins  ?? 1);
  const barsForBB     = aggregateBars(bars, bbCfg.timeframeMins   ?? 5);

  const macd    = calcMACD(barsForMACD.map(b=>b.close),   macdCfg.fast??12, macdCfg.slow??26, macdCfg.signal??9);
  const maCross = calcMACross(barsForMA.map(b=>b.close),  maCfg.fastPeriod??9, maCfg.slowPeriod??20);
  const stoch   = calcStochastic(barsForStoch,             stochCfg.kPeriod??14, stochCfg.dPeriod??3);
  const rsi     = calcRSI(barsForRSI.map(b=>b.close),     rsiCfg.period??14);
  const bbW     = calcBBWidth(barsForBB.map(b=>b.close));

  // VWAP cross: detect if last bar crossed VWAP against prior bar
  const vwapCrossed = (() => {
    if (!vwap || bars.length < 2) return null;
    const band = vwap * ((indCfg.vwapCross?.bandPct ?? 0.05) / 100);
    const cur = bars[bars.length-1]?.close, prv = bars[bars.length-2]?.close;
    if (cur == null || prv == null) return null;
    if (prv > vwap + band && cur < vwap - band) return 'below';
    if (prv < vwap - band && cur > vwap + band) return 'above';
    return null;
  })();

  const allInds = { macd, maCross, stoch, rsi, vwapCrossed, bbWidth: bbW };

  // 5-min candles for Strategy B (VWAP/EMA entry) — always on 5-min
  const candles5 = barsForMA; // already 5-min
  const closes5  = candles5.map(c => c.close);
  const ema9on5  = calcEMA(closes5, 9);

  const orbBars = bars.slice(0, 15);
  const orbHigh = orbBars.length ? Math.max(...orbBars.map(b => b.high)) : null;
  const orbLow  = orbBars.length ? Math.min(...orbBars.map(b => b.low))  : null;
  const orbDir  = minutesIn >= 15 && orbHigh && orbLow
    ? (price > orbHigh + orbHigh*0.0005 ? 'up' : price < orbLow - orbLow*0.0005 ? 'down' : null) : null;

  const last2  = candles5.slice(-2);
  const emaDir = ema9on5 && last2.length === 2 && vwap
    ? (last2.every(c => c.close > vwap && c.close > ema9on5) ? 'up'
     : last2.every(c => c.close < vwap && c.close < ema9on5) ? 'down' : null) : null;

  const exitSignals = calcExitSignals(pos, price, allInds, vwap, atrPct, alertCfg, instrument);

  // Session regime for spread type suggestion (trend → debit, range/grind → credit)
  const regime = (bars.length >= 10 && marketOpen)
    ? classifySessionRegime(bars, liveState?.adx, vwap)
    : null;

  // Suggested spread type based on regime:
  // Trend day (ORB break + confirmed) → debit spread in trend direction
  // Range / slow grind → credit spread (directional for grind)
  const regimeIsTrend  = regime?.regime === 'trend';
  const regimeIsGrind  = regime && !regimeIsTrend && regime.metrics?.netMovePct != null && Math.abs(regime.metrics.netMovePct) > 0.2;
  const regimeIsRange  = !regimeIsTrend;

  let suggestedType = '';
  if (regimeIsTrend) {
    // Trend → buy debit in break direction
    suggestedType = orbDir==='up'||emaDir==='up' ? 'call_debit' : orbDir==='down'||emaDir==='down' ? 'put_debit' : '';
  } else {
    // Range or grind → sell credit in the direction that benefits
    suggestedType = orbDir==='up'||emaDir==='up' ? 'bull_put' : orbDir==='down'||emaDir==='down' ? 'bear_call' : '';
  }

  // Strikes for debit vs credit
  // For SPX: prices are ~10× SPY. Scale the price reference for strike suggestions.
  const priceForStrikes = instrument === 'SPX' ? (price ?? 0) * 10 : price;
  const w = spreadWidth(atrPct, priceForStrikes ?? price);
  let suggestedSell = null, suggestedBuy = null;
  if (suggestedType === 'call_debit') {
    // Buy ATM call spread: long near current price, short higher
    const longS  = price ? ceilStrike(price) : null;
    suggestedSell = longS; suggestedBuy = longS ? longS + w : null; // repurpose fields: sell=long strike (buy leg), buy=short strike
  } else if (suggestedType === 'put_debit') {
    const longS  = price ? floorStrike(price) : null;
    suggestedSell = longS; suggestedBuy = longS ? longS - w : null;
  } else if (suggestedType === 'bull_put' && orbLow) {
    suggestedSell = floorStrike(orbLow)-1; suggestedBuy = suggestedSell - w;
  } else if (suggestedType === 'bear_call' && orbHigh) {
    suggestedSell = ceilStrike(orbHigh)+1; suggestedBuy = suggestedSell + w;
  }

  // Alert firing
  const notifiedRef = useRef(new Set());
  const lastFireRef = useRef({});
  useEffect(() => {
    if (!alertCfg.enabled || !pos.active || !exitSignals.length) return;
    const nowMs = Date.now();
    const cooldownMs = (alertCfg.cooldownMins??5)*60_000;
    for (const sig of exitSignals) {
      const typeKey = sig.level.replace(/ /g,'_');
      const typeCfg = alertCfg.alertTypes[typeKey];
      if (!typeCfg?.enabled) continue;
      const dedupeKey = `${typeKey}:${sig.text.slice(0,50)}`;
      const lastFired = lastFireRef.current[typeKey]??0;
      if (nowMs-lastFired < cooldownMs) continue;
      if (notifiedRef.current.has(dedupeKey)) continue;
      notifiedRef.current.add(dedupeKey);
      lastFireRef.current[typeKey] = nowMs;
      if (alertCfg.audioEnabled) {
        if (ringtoneRef.current) playAudio(ringtoneRef.current, { targetVol:alertCfg.volume, cutoffSecs:alertCfg.ringtoneCutoffSecs, gradual:alertCfg.gradualVolume });
        else playBeep(typeCfg.severity, alertCfg.volume);
      }
      if (alertCfg.browserNotif && Notification?.permission==='granted') {
        new Notification(`${instrument} Safe Day — ${sig.level}`, { body:sig.text, icon:'/favicon.ico', tag:dedupeKey });
      }
    }
  }, [exitSignals.map(s=>s.level+s.text.slice(0,20)).join('|'), alertCfg.enabled, pos.active]);

  useEffect(() => { notifiedRef.current=new Set(); lastFireRef.current={}; }, [marketOpen]);

  if (!engineOn) return (
    <div style={{ textAlign:'center', color:'var(--faint)', fontSize:12, padding:'40px 20px' }}>
      Press <strong style={{ color:'var(--muted)' }}>Start</strong> to begin monitoring SPY.
    </div>
  );
  // When market is closed: still render pre-market direction + advice, gate intraday panels
  const intraday = marketOpen && bars.length > 0 && price != null;

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:16, padding:'8px 4px', marginBottom:4, flexWrap:'wrap' }}>
        <div>
          <span style={{ fontFamily:'monospace', fontWeight:800, fontSize:15, color:'var(--text)' }}>
            {instrument}
          </span>

          {price!=null && <span style={{ fontFamily:'monospace', fontSize:13, color:'var(--faint)', marginLeft:8 }}>${price.toFixed(2)}</span>}
        </div>
        <span style={{ fontSize:10, color:'var(--faint)', fontFamily:'monospace' }}>{minutesIn}min into session</span>
        {vwap   && <span style={{ fontSize:10, color:'var(--warn)',  fontFamily:'monospace' }}>VWAP ${vwap.toFixed(2)}</span>}
        {atrPct && <span style={{ fontSize:10, color:'var(--faint)', fontFamily:'monospace' }}>ATR {atrPct.toFixed(3)}% ≈ ${((atrPct/100)*(price??0)).toFixed(2)}/bar</span>}
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:10 }}>
            <span style={{ width:7, height:7, borderRadius:'50%', background: alertCfg.enabled ? 'var(--up)' : 'var(--faint)', display:'inline-block' }} />
            <span style={{ color: alertCfg.enabled ? 'var(--up)' : 'var(--faint)' }}>{alertCfg.enabled ? 'Alerts on' : 'Alerts off'}</span>
          </div>
          <button onClick={() => setShowSettings(s=>!s)}
            style={{ fontSize:10, padding:'3px 9px', border:'1px solid var(--border)', borderRadius:4,
              background: showSettings ? 'var(--border)' : 'transparent', color:'var(--muted)', cursor:'pointer' }}>
            ⚙ {showSettings ? 'Hide settings' : 'Alert settings'}
          </button>
        </div>
      </div>

      {showSettings && <SafeDayAlertSettings settings={globalSettings?.safeDayAlerts} onSave={saveAlertSettings} />}

      {/* SPY Pre-market direction — always shown, even before market open */}
      <SpyPreMarketPanel pm={pm} vixPrice={vixPrice} marketOpen={marketOpen} loading={pmLoading} />

      <AdvicePanel liveState={{ ...liveState, preMarket: pm }} vixQuote={vixQuote ?? null} marketOpen={marketOpen} />

      {/* Market closed hint */}
      {!marketOpen && (
        <div style={{ textAlign:'center', padding:'16px', borderRadius:8, border:'1px solid var(--border)',
          background:'var(--bg)', color:'var(--faint)', fontSize:11, lineHeight:1.8, marginBottom:14 }}>
          <div style={{ fontSize:15, marginBottom:6 }}>🕙 Market closed</div>
          Pre-market direction above is based on the last session's data.<br/>
          Intraday indicators activate at <strong style={{ color:'var(--text)' }}>9:30 ET</strong> once the engine is running.
        </div>
      )}

      {/* Intraday panels — only when market is open and bars are available */}
      {intraday && (
        <>
          <CombinedSignal orbDir={orbDir} emaDir={emaDir} />
          {pos.active && exitSignals.length>0 && <ExitSignalSummary signals={exitSignals} pos={pos} />}
          <StrategyA bars={bars} minutesIn={minutesIn} price={price} atrPct={atrPct} ticker={instrument} />
          <StrategyB bars={bars} minutesIn={minutesIn} price={price} vwap={vwap} atrPct={atrPct} ticker={instrument} />
        </>
      )}
      {!intraday && marketOpen && (
        <div style={{ textAlign:'center', color:'var(--faint)', fontSize:11, padding:'14px 0' }}>
          Accumulating price bars…
        </div>
      )}

      {/* GEX levels — always shown once price is available */}
      <GEXPanel price={price} vwap={vwap} atrPct={atrPct} ticker={instrument} bars={bars} pm={pm} marketOpen={marketOpen} />

      {/* 0DTE flow — manual input panel */}
      <ZeroDTEFlowPanel ticker={instrument} flowKey="sd:0dte" />

      <div style={{ margin:'4px 0 8px', borderTop:'1px solid var(--border)', paddingTop:14 }}>
        <div style={{ fontSize:11, fontWeight:800, letterSpacing:'.06em', color:'var(--text)', marginBottom:12 }}>EXIT PLAN</div>

        {/* IBKR-style options chain picker */}
        <OptionsChainPicker price={price} ticker={instrument} pos={pos} onChange={handlePosChange}
          expiryLabel="Today (0DTE)" />

        {pos.active && !exitSignals.length && (
          <div style={{ padding:'10px 16px', borderRadius:8, border:'1px solid var(--up)', background:'#22c55e0e', marginBottom:14 }}>
            <span style={{ fontSize:12, fontWeight:700, color:'var(--up)' }}>✓ Position looks fine</span>
            <span style={{ fontSize:11, color:'var(--muted)', marginLeft:10 }}>No exit signals. Continue monitoring.</span>
          </div>
        )}
        {intraday && (
          <>
            <MACrossPanel maCross={maCross} posType={pos.active ? pos.type : ''} />
            <MACDPanel    macd={macd}       posType={pos.active ? pos.type : ''} />
          </>
        )}
      </div>

      <div style={{ fontSize:9, color:'var(--faint)', textAlign:'center', padding:'10px 0', lineHeight:1.6 }}>
        Strikes are indicative — verify with the live option chain before entering. Safe Day is SPX only. Strikes are indicative — verify with live chain before entering. P&L requires live options data. Not investment advice.
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// GEX PANEL — Gamma Exposure levels (estimated from price + ATR/volatility)
// No live GEX feed: levels are *estimated* structural reference points.
// Call Wall ≈ price + 1.5×daily ATR | Put Wall ≈ price − 1.5×daily ATR
// Gamma Flip ≈ VWAP (intraday pivot where dealer hedging reverses)
// ═══════════════════════════════════════════════════════════════════════════════
export function GEXPanel({ price, vwap, atrPct, ticker = 'SPX', bars = [], pm = null, marketOpen = true }) {
  // Pre-market mode: use prior day close + range as the reference price + ATR proxy
  const isPreMarket = !marketOpen || !price;
  const refPrice  = isPreMarket ? (pm?.prevClose ?? null) : price;
  const pmPrice   = pm?.prevClose ?? null; // yesterday's close always available when pm is set

  if (!refPrice) return null;

  // Daily ATR:
  //   Intraday: scale atrPct × 6 bars ≈ 1 full day
  //   Pre-market: use prior day range (high - low) as ATR proxy, fall back to 1.2%
  const dailyAtrPct = isPreMarket
    ? (pm?.priorHigh && pm?.priorLow && pm.prevClose
        ? ((pm.priorHigh - pm.priorLow) / pm.prevClose * 100)
        : 1.2)
    : (atrPct ? Math.max(atrPct * 6, 0.8) : 1.2);
  const dailyAtr = refPrice * dailyAtrPct / 100;

  // Strike increment for this instrument
  const inc = ticker === 'SPX' || ticker === 'SPXW' ? 5
            : ticker === 'SPY' ? 1
            : price > 500 ? 5 : price > 100 ? 2.5 : 1;

  const snapStrike = (p) => Math.round(p / inc) * inc;

  const callWall  = snapStrike(price + dailyAtr * 1.5);
  const putWall   = snapStrike(price - dailyAtr * 1.5);
  // Gamma flip: intraday = VWAP; pre-market = prior close (best structural reference pre-open)
  const gammaFlip = isPreMarket
    ? snapStrike(pmPrice ?? refPrice)
    : (vwap ? snapStrike(vwap) : snapStrike(refPrice));

  const aboveFlip = refPrice >= gammaFlip;
  const nearCall  = Math.abs(refPrice - callWall) / refPrice < 0.005;
  const nearPut   = Math.abs(refPrice - putWall)  / refPrice < 0.005;

  const regime = aboveFlip
    ? { label: 'POSITIVE GAMMA', short: 'Positive Γ', color: '#22c55e', icon: '📦',
        layman: 'Market makers are buying dips and selling rallies — this creates a calming, range-bound effect. Good for selling credit spreads (premium decay works in your favor).',
        trade:  'Range day likely. Sell credit spreads just outside the Call Wall (resistance) or Put Wall (support). Credit works better than debit here.' }
    : { label: 'NEGATIVE GAMMA', short: 'Negative Γ', color: '#ef4444', icon: '⚡',
        layman: 'Market makers are now chasing moves — they buy rallies AND sell dips, amplifying every swing. Expect explosive trends and large candles.',
        trade:  'Trend day likely. Buy debit spreads in the direction of the break. Avoid selling premium — the move can accelerate hard against you.' };

  const levels = [
    { name: 'Call Wall', price: callWall, color: '#ef4444', icon: '🔴',
      layman: 'Heavy resistance. Market makers sell shares here to hedge call options they sold. Price often stalls or reverses at this level.' },
    { name: 'Gamma Flip', price: gammaFlip, color: '#f59e0b', icon: '⚡',
      layman: 'The pivot. Above = calm ranging; below = explosive trends. Watch for a 5-min confirmed break before committing.' },
    { name: 'Put Wall', price: putWall, color: '#22c55e', icon: '🟢',
      layman: 'Strong floor. Market makers buy shares here to hedge puts they sold. Price often bounces from this level.' },
  ].sort((a,b) => b.price - a.price);

  const barW = (p) => {
    const range = callWall - putWall || 1;
    return Math.max(5, Math.min(95, ((p - putWall) / range) * 100));
  };

  return (
    <div style={{ border: `1px solid ${regime.color}40`, borderRadius: 8, padding: '12px 14px', marginBottom: 12, background: regime.color + '08' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.1em', color: 'var(--faint)', textTransform: 'uppercase' }}>
            {ticker} · GEX Levels
            <span style={{ fontWeight: 400, fontStyle: 'italic' }}> (estimated{isPreMarket ? ' · pre-market, based on prior close' : ''})</span>
          </span>
          <span style={{ fontSize: 10, fontWeight: 800, color: regime.color, padding: '1px 7px', borderRadius: 3,
            border: `1px solid ${regime.color}50`, background: regime.color + '14' }}>
            {regime.icon} {regime.short}
          </span>
        </div>
        <span style={{ fontSize: 9, color: 'var(--faint)', fontStyle: 'italic' }}>
          No live GEX feed — levels derived from ATR + VWAP
        </span>
      </div>

      {/* Regime explanation */}
      <div style={{ fontSize: 11, color: 'var(--text)', lineHeight: 1.6, marginBottom: 10,
        padding: '8px 10px', borderRadius: 6, background: 'var(--bg)', border: '1px solid var(--border)' }}>
        <strong style={{ color: regime.color }}>{regime.label}:</strong> {regime.layman}
      </div>

      {/* Price map */}
      <div style={{ position: 'relative', marginBottom: 10 }}>
        <div style={{ height: 28, background: 'var(--bg)', borderRadius: 4, border: '1px solid var(--border)', position: 'relative', overflow: 'hidden' }}>
          {/* Gradient fill */}
          <div style={{ position: 'absolute', inset: 0,
            background: `linear-gradient(to right, #22c55e15, #f59e0b15 ${barW(gammaFlip)}%, #ef444415)` }} />
          {/* Level markers */}
          {levels.map(l => (
            <div key={l.name} style={{ position: 'absolute', top: 0, bottom: 0, width: 2,
              left: `${barW(l.price)}%`, background: l.color, opacity: 0.7 }} />
          ))}
          {/* Reference price dot */}
          <div style={{ position: 'absolute', top: 2, bottom: 2, width: 2,
            left: `${barW(refPrice)}%`, background: 'var(--text)', borderRadius: 1 }}>
            <div style={{ position: 'absolute', top: -2, left: -4, width: 10, height: 10,
              borderRadius: '50%', background: 'var(--text)', border: '2px solid var(--bg)' }} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 8, color: 'var(--faint)', marginTop: 2 }}>
          <span>Put Wall ${putWall}</span><span>Flip ${gammaFlip}</span><span>Call Wall ${callWall}</span>
        </div>
      </div>

      {/* Level cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 10 }}>
        {levels.map(l => {
          const dist = ((refPrice - l.price) / refPrice * 100).toFixed(2);
          const away = Math.abs(dist);
          const approaching = away < 0.3;
          return (
            <div key={l.name} style={{ padding: '8px 10px', borderRadius: 6,
              border: `1px solid ${approaching ? l.color : 'var(--border)'}`,
              background: approaching ? l.color + '10' : 'var(--bg)' }}>
              <div style={{ fontSize: 9, color: 'var(--faint)', marginBottom: 2 }}>{l.icon} {l.name}</div>
              <div style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 800, color: l.color }}>${l.price}</div>
              <div style={{ fontSize: 9, color: approaching ? l.color : 'var(--faint)', fontWeight: approaching ? 700 : 400 }}>
                {approaching ? '⚠ approaching' : `${dist > 0 ? '+' : ''}${dist}% away`}
              </div>
              <div style={{ fontSize: 9, color: 'var(--faint)', lineHeight: 1.4, marginTop: 4 }}>{l.layman}</div>
            </div>
          );
        })}
      </div>

      {/* Trade implication */}
      <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
        <strong style={{ color: 'var(--text)' }}>{isPreMarket ? 'Pre-market setup:' : "Today's trade implication:"}</strong>{' '}
        {isPreMarket ? `Levels based on yesterday's close $${refPrice}. Verify after open — actual flip may shift if today gaps significantly. ` : ''}{regime.trade}
        {nearCall && <div style={{ color: '#ef4444', fontWeight: 700, marginTop: 4 }}>⚠ Price near Call Wall — watch for reversal or gamma squeeze if volume surges.</div>}
        {nearPut  && <div style={{ color: '#22c55e', fontWeight: 700, marginTop: 4 }}>⚠ Price near Put Wall — watch for mechanical bounce from dealer buying.</div>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ZERO-DTE FLOW PANEL — Manual 0DTE put/call volume tracker
// User inputs call and put volume; dashboard shows ratio and implication.
// ═══════════════════════════════════════════════════════════════════════════════
export function ZeroDTEFlowPanel({ ticker = 'SPX', flowKey = 'sd:0dte' }) {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [lastFetch,setLastFetch]= useState(null);
  const [manualCv, setManualCv] = useState('');
  const [manualPv, setManualPv] = useState('');
  const [manual,   setManual]   = useState(false);

  const fetchFlow = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await ibkrFetchOptionFlow(ticker);
      setData(d); setLastFetch(Date.now());
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, [ticker]);

  useEffect(() => { fetchFlow(); const id = setInterval(fetchFlow, 5 * 60 * 1000); return () => clearInterval(id); }, [ticker]); // eslint-disable-line

  const cv = manual ? (parseFloat(manualCv.replace(/,/g,'')) || 0) : (data?.callVolume || 0);
  const pv = manual ? (parseFloat(manualPv.replace(/,/g,'')) || 0) : (data?.putVolume  || 0);
  const total   = cv + pv;
  const ratio   = pv > 0 ? (cv / pv) : cv > 0 ? Infinity : null;
  const callPct = total ? Math.round(cv / total * 100) : 0;
  const putPct  = total ? Math.round(pv / total * 100) : 0;
  const ageMins = lastFetch ? Math.round((Date.now() - lastFetch) / 60000) : null;

  const getBias = (r) => {
    if (r === null) return null;
    if (r >= 3)   return { label: 'STRONGLY BULLISH', color: '#22c55e', icon: '↑↑',
      layman: `Call volume is ${r.toFixed(1)}× puts. Market makers aggressively buy shares to hedge — strongly favors upward drift. Favor call debit spreads or bull put credit spreads.` };
    if (r >= 2)   return { label: 'BULLISH', color: '#4ade80', icon: '↑',
      layman: `C/P ratio ${r.toFixed(1)} — moderate bullish lean. Upward drift likely. Wait for price confirmation before sizing up.` };
    if (r >= 1.3) return { label: 'SLIGHTLY BULLISH', color: '#86efac', icon: '↗',
      layman: 'Slight call dominance. No strong edge — wait for price action to confirm.' };
    if (r <= 0.33)return { label: 'STRONGLY BEARISH', color: '#ef4444', icon: '↓↓',
      layman: `Put volume ${(1/r).toFixed(1)}× calls. MM forced to sell shares to hedge — favors downward acceleration. Favor put debit spreads or bear call credit spreads.` };
    if (r <= 0.5) return { label: 'BEARISH', color: '#f87171', icon: '↓',
      layman: 'Put dominance — bearish lean. Downward drift favored.' };
    return { label: 'NEUTRAL', color: '#f59e0b', icon: '→',
      layman: 'Balanced flow. No directional edge from volume. Wait for price confirmation.' };
  };
  const bias = total > 0 ? getBias(ratio) : null;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginBottom: 12, background: 'var(--bg)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.1em', color: 'var(--faint)', textTransform: 'uppercase' }}>
            {ticker} · Options Flow
          </span>
          {data?.expiryDate && !manual && <span style={{ fontSize: 9, color: 'var(--faint)', fontFamily: 'monospace' }}>chain exp {data.expiryDate}</span>}
          {manual && <span style={{ fontSize: 9, color: 'var(--warn)', padding: '1px 6px', border: '1px solid var(--warn)', borderRadius: 3 }}>manual</span>}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {ageMins !== null && !manual && <span style={{ fontSize: 9, color: 'var(--faint)', fontFamily: 'monospace' }}>{ageMins < 1 ? 'just now' : `${ageMins}m ago`} · 5m auto</span>}
          <button onClick={() => setManual(m => !m)} style={{ fontSize: 9, padding: '2px 8px', border: '1px solid var(--border)', borderRadius: 3, background: manual ? 'var(--border)' : 'transparent', color: 'var(--muted)', cursor: 'pointer' }}>
            {manual ? 'Auto' : 'Manual'}
          </button>
          {!manual && <button onClick={fetchFlow} disabled={loading} style={{ fontSize: 9, padding: '2px 8px', border: '1px solid var(--border)', borderRadius: 3, background: 'transparent', color: 'var(--muted)', cursor: loading ? 'default' : 'pointer' }}>{loading ? '⟳' : '↺'}</button>}
        </div>
      </div>

      {error && !manual && <div style={{ fontSize: 10, color: 'var(--down)', marginBottom: 8 }}>⚠ {error} — switch to Manual to enter values.</div>}

      {manual && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 9, color: '#22c55e', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.06em' }}>Call Volume</div>
            <input type="text" inputMode="numeric" placeholder="e.g. 45000" value={manualCv} onChange={e => setManualCv(e.target.value)}
              style={{ width: 110, padding: '6px 9px', background: 'var(--bg)', border: '1px solid #22c55e50', borderRadius: 5, color: '#22c55e', fontSize: 12, fontFamily: "'IBM Plex Mono',monospace", fontWeight: 700 }} />
          </div>
          <div>
            <div style={{ fontSize: 9, color: '#ef4444', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.06em' }}>Put Volume</div>
            <input type="text" inputMode="numeric" placeholder="e.g. 15000" value={manualPv} onChange={e => setManualPv(e.target.value)}
              style={{ width: 110, padding: '6px 9px', background: 'var(--bg)', border: '1px solid #ef444450', borderRadius: 5, color: '#ef4444', fontSize: 12, fontFamily: "'IBM Plex Mono',monospace", fontWeight: 700 }} />
          </div>
          <button onClick={() => { setManualCv(''); setManualPv(''); }} style={{ fontSize: 9, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 4, background: 'transparent', color: 'var(--faint)', cursor: 'pointer' }}>Clear</button>
        </div>
      )}

      {!manual && loading && !data && <div style={{ fontSize: 11, color: 'var(--faint)', fontStyle: 'italic', marginBottom: 8 }}>⟳ Fetching option flow…</div>}

      {total > 0 && (
        <>
          <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 9, color: '#22c55e', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 2 }}>Calls</div>
              <div style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 900, color: '#22c55e' }}>{cv.toLocaleString()}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 9, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 2 }}>C/P Ratio</div>
              <div style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 900, color: bias?.color ?? 'var(--text)' }}>{ratio === Infinity ? '∞' : ratio?.toFixed(2) ?? '—'}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 9, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 2 }}>Puts</div>
              <div style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 900, color: '#ef4444' }}>{pv.toLocaleString()}</div>
            </div>
          </div>
          <div style={{ height: 8, borderRadius: 4, overflow: 'hidden', display: 'flex', marginBottom: 8 }}>
            <div style={{ width: `${callPct}%`, background: '#22c55e', transition: 'width .4s' }} />
            <div style={{ width: `${putPct}%`,  background: '#ef4444', transition: 'width .4s' }} />
          </div>
        </>
      )}

      {bias ? (
        <div style={{ padding: '8px 10px', borderRadius: 6, border: `1px solid ${bias.color}40`, background: bias.color + '0e' }}>
          <div style={{ fontSize: 13, fontWeight: 900, color: bias.color, marginBottom: 4 }}>{bias.icon} {bias.label}</div>
          <div style={{ fontSize: 11, color: 'var(--text)', lineHeight: 1.6 }}>{bias.layman}</div>
        </div>
      ) : !loading && (
        <div style={{ fontSize: 11, color: 'var(--faint)', fontStyle: 'italic' }}>{error ? 'Switch to Manual to enter values.' : 'Loading…'}</div>
      )}

      {!manual && <div style={{ fontSize: 9, color: 'var(--faint)', marginTop: 8, lineHeight: 1.5 }}>
        Source: Yahoo Finance nearest expiry chain (all expirations). For 0DTE-only volume, switch to Manual and enter from IBKR → Options Chain → today's expiry → volume column.
      </div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// IBKR-STYLE OPTIONS CHAIN PICKER — ±15 strikes, select legs to build spread
// User clicks to select up to 2 legs (long + short), enters lots and price,
// then hits "Track" to set up exit alerts.
// ═══════════════════════════════════════════════════════════════════════════════
function strikeInc(ticker, price) {
  if (ticker === 'SPX' || ticker === 'SPXW') return 5;
  if (ticker === 'SPY') return 1;
  if (price > 1000) return 10;
  if (price > 400)  return 5;
  if (price > 100)  return 2.5;
  return 1;
}

function roundToInc(price, inc) { return Math.round(price / inc) * inc; }

export function OptionsChainPicker({ price, ticker = 'SPX', pos, onChange, expiryLabel = 'Today (0DTE)' }) {
  const [mode,  setMode]    = useState('call');   // 'call' | 'put'
  const [style, setStyle]   = useState('debit');  // 'debit' | 'credit'
  const [legs, setLegs]     = useState([]); // [{strike, role:'long'|'short'}]
  const [lots, setLots]     = useState('1');
  const [legPrices, setLegPrices] = useState({}); // strike → price string
  const [confirmed, setConfirmed] = useState(false);

  // Reset if price changes significantly (new day)
  const prevPriceRef = React.useRef(price);
  React.useEffect(() => {
    if (price && prevPriceRef.current && Math.abs(price - prevPriceRef.current) / prevPriceRef.current > 0.02) {
      setLegs([]); setLegPrices({}); setConfirmed(false);
    }
    prevPriceRef.current = price;
  }, [price]);

  if (!price) return (
    <div style={{ padding: '14px 16px', borderRadius: 8, border: '1px solid var(--border)', marginBottom: 12,
      fontSize: 11, color: 'var(--faint)', fontStyle: 'italic' }}>
      Options chain loads once live price is available.
    </div>
  );

  const inc    = strikeInc(ticker, price);
  const center = roundToInc(price, inc);
  const strikes = Array.from({ length: 31 }, (_, i) => center + (i - 15) * inc);

  const toggleLeg = (strike) => {
    setConfirmed(false);
    setLegs(prev => {
      const existing = prev.find(l => l.strike === strike);
      if (existing) return prev.filter(l => l.strike !== strike); // deselect
      if (prev.length === 0) return [{ strike, role: 'long' }];
      if (prev.length === 1) {
        const first = prev[0];
        const lo = Math.min(first.strike, strike);
        const hi = Math.max(first.strike, strike);
        // Debit spreads: buy the closer-to-money leg, sell the further leg
        //   Call debit: long lower, short higher
        //   Put  debit: long higher, short lower
        // Credit spreads: sell the closer-to-money leg, buy the further leg
        //   Call credit: short lower, long higher
        //   Put  credit: short higher, long lower
        let roles;
        if (style === 'debit') {
          roles = mode === 'call'
            ? [{ strike: lo, role: 'long'  }, { strike: hi, role: 'short' }]
            : [{ strike: hi, role: 'long'  }, { strike: lo, role: 'short' }];
        } else {
          roles = mode === 'call'
            ? [{ strike: lo, role: 'short' }, { strike: hi, role: 'long'  }]
            : [{ strike: hi, role: 'short' }, { strike: lo, role: 'long'  }];
        }
        return roles;
      }
      // 3rd click → replace all
      return [{ strike, role: 'long' }];
    });
  };

  const longLeg  = legs.find(l => l.role === 'long');
  const shortLeg = legs.find(l => l.role === 'short');
  const longP    = parseFloat(legPrices[longLeg?.strike] ?? '') || 0;
  const shortP   = parseFloat(legPrices[shortLeg?.strike] ?? '') || 0;
  const netDebit = longLeg && shortLeg ? (longP - shortP) : longP;
  const width    = longLeg && shortLeg ? Math.abs(longLeg.strike - shortLeg.strike) : 0;
  const numLots  = parseInt(lots) || 1;

  // Determine spread type first so isCreditSpread is available for P&L calculations
  // For calls: long lower + short higher = bull call debit; long higher + short lower = bear call credit
  // For puts:  long higher + short lower = bear put debit; long lower + short higher = bull put credit
  const spreadLabel = legs.length === 2 && longLeg && shortLeg
    ? (mode === 'call'
        ? (longLeg.strike < shortLeg.strike ? 'Bull Call Debit Spread'  : 'Bear Call Credit Spread')
        : (longLeg.strike > shortLeg.strike ? 'Bear Put Debit Spread'   : 'Bull Put Credit Spread'))
    : null;
  const isCreditSpread = !!(spreadLabel?.includes('Credit'));

  const maxProfit = legs.length === 2
    ? (isCreditSpread ? Math.abs(netDebit) * 100 * numLots : (width - Math.abs(netDebit)) * 100 * numLots).toFixed(0)
    : null;
  const maxLoss = legs.length === 2
    ? (isCreditSpread ? (width - Math.abs(netDebit)) * 100 * numLots : Math.abs(netDebit) * 100 * numLots).toFixed(0)
    : null;

  const handleTrack = () => {
    if (!longLeg || legs.length < 2) return;
    const isDebit = !isCreditSpread;
    const posType = mode === 'call'
      ? (isDebit ? 'call_debit' : 'bear_call')
      : (isDebit ? 'put_debit'  : 'bull_put');
    onChange({
      type: posType,
      longStrike:  String(longLeg.strike),
      shortStrike: String(shortLeg.strike),
      premium:     String(Math.abs(netDebit).toFixed(2)),
      contracts:   numLots,
      active: true,
    });
    setConfirmed(true);
  };

  // Color helpers
  const ITM_CALL = '#22c55e18'; const OTM_CALL = 'transparent';
  const ITM_PUT  = '#ef444418'; const OTM_PUT  = 'transparent';

  const rowBg = (strike) => {
    const isItmCall = mode === 'call' && strike <= price;
    const isItmPut  = mode === 'put'  && strike >= price;
    const leg = legs.find(l => l.strike === strike);
    if (leg) return leg.role === 'long' ? '#3b82f620' : '#f59e0b20';
    if (isItmCall) return ITM_CALL;
    if (isItmPut)  return ITM_PUT;
    return OTM_CALL;
  };
  const isATM = (strike) => Math.abs(strike - price) < inc * 0.6;

  // Approximate IV% → estimated bid/ask (very rough: use ATR as vol proxy)
  // For display only — always verify with live chain
  const estPremium = (strike, isLong) => {
    const distPct = Math.abs(strike - price) / price * 100;
    const ivProxy = 0.20; // 20% IV assumption — rough
    const dte = 1 / 252;
    const intrinsic = mode === 'call' ? Math.max(0, price - strike) : Math.max(0, strike - price);
    const timeVal   = price * ivProxy * Math.sqrt(dte) * Math.exp(-distPct * 0.4);
    const est = intrinsic + Math.max(0.05, timeVal);
    const spread = Math.max(0.05, est * 0.08); // ~8% bid-ask
    return isLong ? (est + spread / 2).toFixed(2) : (est - spread / 2).toFixed(2);
  };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '10px 14px', background: 'var(--bg-elev,var(--bg))', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 800, fontSize: 12, color: 'var(--text)' }}>{ticker} · {expiryLabel}</span>
        <span style={{ fontSize: 10, color: 'var(--faint)', fontFamily: 'monospace' }}>Spot ${price.toFixed(2)}</span>
        <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
          {['call', 'put'].map(m => (
            <button key={m} onClick={() => { setMode(m); setLegs([]); setLegPrices({}); setConfirmed(false); }}
              style={{ fontSize: 10, padding: '3px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', fontWeight: 700,
                background: mode === m ? (m === 'call' ? '#22c55e' : '#ef4444') : 'var(--border)',
                color: mode === m ? '#fff' : 'var(--faint)' }}>
              {m.toUpperCase()}S
            </button>
          ))}
          <div style={{ width: 1, height: 16, background: 'var(--border)', margin: '0 2px' }} />
          {['debit', 'credit'].map(s => (
            <button key={s} onClick={() => { setStyle(s); setLegs([]); setLegPrices({}); setConfirmed(false); }}
              style={{ fontSize: 10, padding: '3px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', fontWeight: 700,
                background: style === s ? (s === 'debit' ? '#3b82f6' : '#f59e0b') : 'var(--border)',
                color: style === s ? '#fff' : 'var(--faint)' }}>
              {s === 'debit' ? 'DEBIT' : 'CREDIT'}
            </button>
          ))}
        </div>
        <span style={{ fontSize: 9, color: 'var(--faint)', fontStyle: 'italic' }}>
          Prices estimated — verify with live chain
        </span>
      </div>

      {/* Column headers */}
      <div style={{ display: 'grid', gridTemplateColumns: '70px 70px 70px 1fr 70px 70px',
        padding: '4px 10px', background: 'var(--bg)', borderBottom: '1px solid var(--border)',
        fontSize: 9, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
        <span>Bid</span><span>Ask</span><span>Est. IV</span>
        <span style={{ textAlign: 'center' }}>STRIKE</span>
        <span style={{ textAlign: 'right' }}>Leg</span>
        <span style={{ textAlign: 'right' }}>My Price</span>
      </div>

      {/* Strike rows */}
      <div style={{ maxHeight: 420, overflowY: 'auto' }}>
        {strikes.map(strike => {
          const leg       = legs.find(l => l.strike === strike);
          const isAtm     = isATM(strike);
          const bid       = parseFloat(estPremium(strike, false));
          const ask       = parseFloat(estPremium(strike, true));
          const ivEst     = Math.max(10, Math.round((Math.abs(strike - price) / price * 100 * 2 + 18)));
          const legColor  = leg ? (leg.role === 'long' ? '#3b82f6' : '#f59e0b') : null;

          return (
            <div key={strike}
              onClick={() => toggleLeg(strike)}
              style={{ display: 'grid', gridTemplateColumns: '70px 70px 70px 1fr 70px 70px',
                padding: '5px 10px', cursor: 'pointer', alignItems: 'center',
                background: rowBg(strike),
                borderBottom: isAtm ? '2px solid var(--accent)' : '1px solid var(--border)',
                outline: leg ? `1px solid ${legColor}` : 'none',
                transition: 'background .15s' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 11, color: leg ? legColor : '#22c55e' }}>{bid.toFixed(2)}</span>
              <span style={{ fontFamily: 'monospace', fontSize: 11, color: leg ? legColor : '#ef4444' }}>{ask.toFixed(2)}</span>
              <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--faint)' }}>{ivEst}%</span>
              <span style={{ textAlign: 'center', fontFamily: 'monospace', fontSize: 13,
                fontWeight: isAtm ? 900 : 600,
                color: isAtm ? 'var(--accent)' : leg ? legColor : 'var(--text)' }}>
                {strike}{isAtm ? ' ←ATM' : ''}
              </span>
              <span style={{ textAlign: 'right', fontSize: 9, fontWeight: 700,
                color: leg ? legColor : 'var(--faint)' }}>
                {leg ? (leg.role === 'long' ? '🟦 LONG' : '🟧 SHORT') : ''}
              </span>
              <span onClick={e => e.stopPropagation()} style={{ textAlign: 'right' }}>
                {leg && (
                  <input type="number" step="0.01" placeholder={leg.role === 'long' ? ask.toFixed(2) : bid.toFixed(2)}
                    value={legPrices[strike] ?? ''}
                    onChange={e => setLegPrices(p => ({ ...p, [strike]: e.target.value }))}
                    style={{ width: 60, padding: '2px 5px', background: 'var(--bg)',
                      border: `1px solid ${legColor}`, borderRadius: 3,
                      color: legColor, fontSize: 10, fontFamily: "'IBM Plex Mono',monospace",
                      textAlign: 'right' }} />
                )}
              </span>
            </div>
          );
        })}
      </div>

      {/* Summary + Track */}
      {legs.length > 0 && (
        <div style={{ padding: '12px 14px', borderTop: '1px solid var(--border)', background: 'var(--bg)' }}>
          {spreadLabel && (
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
              {spreadLabel}
              {legs.length === 2 && <span style={{ fontWeight: 400, color: 'var(--faint)', marginLeft: 8 }}>
                ${longLeg?.strike} / ${shortLeg?.strike} · width ${width}
              </span>}
            </div>
          )}
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            {legs.map(l => (
              <div key={l.strike} style={{ fontSize: 11, color: l.role === 'long' ? '#3b82f6' : '#f59e0b' }}>
                {l.role === 'long' ? '🟦 Buy' : '🟧 Sell'} {mode.toUpperCase()} ${l.strike}
                {legPrices[l.strike] && <span style={{ color: 'var(--faint)', marginLeft: 4 }}>@ ${legPrices[l.strike]}</span>}
              </div>
            ))}
            {legs.length === 2 && (
              <div style={{ fontSize: 11, fontWeight: 700, color: isCreditSpread ? 'var(--up)' : 'var(--down)' }}>
                Net {isCreditSpread ? 'Credit' : 'Debit'}: ${Math.abs(netDebit).toFixed(2)}
                {isCreditSpread && <span style={{ fontSize: 9, color: 'var(--faint)', marginLeft: 6 }}>received</span>}
                {!isCreditSpread && <span style={{ fontSize: 9, color: 'var(--faint)', marginLeft: 6 }}>paid</span>}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 9, color: 'var(--faint)', marginBottom: 2 }}>LOTS</div>
              <input type="number" min={1} value={lots} onChange={e => setLots(e.target.value)}
                style={{ width: 60, padding: '5px 8px', background: 'var(--bg)', border: '1px solid var(--border)',
                  borderRadius: 4, color: 'var(--text)', fontSize: 12, fontFamily: "'IBM Plex Mono',monospace",
                  textAlign: 'center' }} />
            </div>
            {legs.length === 2 && maxProfit && (
              <>
                <div style={{ fontSize: 11, color: 'var(--up)' }}>Max profit: <strong>${maxProfit}</strong></div>
                <div style={{ fontSize: 11, color: 'var(--down)' }}>Max loss: <strong>${maxLoss}</strong></div>
                <div style={{ fontSize: 11, color: 'var(--warn)' }}>
                  {isCreditSpread ? '50% profit target' : '50% target'}: <strong>${(Number(maxProfit) * 0.5).toFixed(0)}</strong>
                  {isCreditSpread && <span style={{ fontSize: 9, color: 'var(--faint)', marginLeft: 4 }}>buy back at ${(Math.abs(netDebit) * 0.5).toFixed(2)}</span>}
                </div>
              </>
            )}
            <button
              disabled={legs.length < 2 || !longLeg || !shortLeg}
              onClick={handleTrack}
              style={{ padding: '7px 18px', borderRadius: 5, border: 'none', fontWeight: 700, fontSize: 12,
                background: legs.length === 2 ? 'var(--accent)' : 'var(--border)',
                color: legs.length === 2 ? 'var(--bg)' : 'var(--faint)', cursor: 'pointer' }}>
              {confirmed ? '✓ Tracking' : 'Track & Set Alerts'}
            </button>
            {legs.length > 0 && (
              <button onClick={() => { setLegs([]); setLegPrices({}); setConfirmed(false); }}
                style={{ fontSize: 10, padding: '5px 10px', border: '1px solid var(--border)', borderRadius: 4,
                  background: 'transparent', color: 'var(--faint)', cursor: 'pointer' }}>Clear</button>
            )}
          </div>
          <div style={{ fontSize: 9, color: 'var(--faint)', marginTop: 8, lineHeight: 1.5 }}>
            Click 2 strikes to build your spread. Enter the price you actually paid in the "My Price" box.
            Hit Track — the exit alert engine will monitor P&L and indicators.
          </div>
        </div>
      )}
    </div>
  );
}

// ── Local styles ──────────────────────────────────────────────────────────────
const fLabel   = { fontSize:9, color:'var(--faint)', marginBottom:3, textTransform:'uppercase', letterSpacing:'.06em' };
const fInput   = { padding:'6px 9px', background:'var(--bg)', border:'1px solid var(--border)',
                   borderRadius:5, color:'var(--text)', fontSize:11, fontFamily:"'IBM Plex Mono',monospace" };
const smallBtn = { fontSize:9, padding:'2px 8px', border:'1px solid var(--border)', borderRadius:3,
                   background:'transparent', color:'var(--faint)', cursor:'pointer' };
